import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import Holidays from './Holidays';
import PTOBlackouts from './PTOBlackouts';
import StaffingRulesAdmin from './StaffingRulesAdmin';
import IntercomPanel from './IntercomPanel';
import UserFormDialog from './UserFormDialog';
import { 
  Settings, 
  Users, 
  Shield, 
  UserPlus, 
  Edit, 
  Trash2, 
  Plus,
  Clock,
  Calendar,
  MapPin,
  Phone,
  DollarSign,
  MessageSquare,
  Target,
  Mail,
  AlertCircle,
  Eye,
  EyeOff,
  CheckCircle,
  Key,
  Plane,
  Heart,
  User,
  Home,
  Building,
  Activity,
  RefreshCw,
  LayoutGrid,
  Cake,
  Monitor,
  Bell,
  ArrowUpRight,
  CalendarOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Switch } from './ui/switch';
import { useAuth } from '../App';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('users');

  // New user-form dialog (modal-with-tabs replaces the old inline form for create/edit)
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userDialogInitial, setUserDialogInitial] = useState(null);
  const openCreateUserDialog = () => { setUserDialogInitial(null); setUserDialogOpen(true); };
  const openEditUserDialog = (user) => { setUserDialogInitial(user); setUserDialogOpen(true); };
  const handleUserDialogSaved = () => { fetchUsers(); };
  
  // Get current user role for access control
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdminOrManager = currentUser.role === 'admin' || currentUser.role === 'manager';
  
  // State for different admin sections
  const [users, setUsers] = useState([]);
  const [systemRoles, setSystemRoles] = useState([]);  // Will be populated dynamically from rolePermissions
  const [roleForm, setRoleForm] = useState({ 
    id: '', 
    name: '', 
    description: '', 
    color: '#3B82F6',
    permissions: ['view_dashboard', 'view_schedules', 'view_pto', 'request_own_pto'] // Default minimum permissions
  });
  const [editingRoleDefinition, setEditingRoleDefinition] = useState(null);
  const [showRoleModal, setShowRoleModal] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [channels, setChannels] = useState(['chat', 'phone', 'email', 'ticket']);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const [lunchSettings, setLunchSettings] = useState({
    duration: 30,
    startTime: '12:00',
    mandatory: true
  });
  
  // Role permissions state
  const [rolePermissions, setRolePermissions] = useState([]);
  const [availablePermissions, setAvailablePermissions] = useState([]);
  const [editingRole, setEditingRole] = useState(null);
  const [permissionChanges, setPermissionChanges] = useState({});

  // Form states
  const [userForm, setUserForm] = useState({
    id: '',
    username: '',
    email: '',
    role: 'tier_1',
    password: '',
    isEditing: false,
    has_schedule: true,  // Default to true - most users have schedules
    work_location: 'office',  // Default to office - remote employees set explicitly
    // Org structure fields
    team_id: '',
    manager_id: '',
    managed_roles: [],  // For team leads: which role teams they manage
    // Employee fields
    name: '',
    phone: '',
    employeeRole: '',
    skills: [],
    support_channels: [],
    availability_start: '09:00',
    availability_end: '17:00',
    shift_preferences: []
  });

  const [channelForm, setChannelForm] = useState({
    name: '',
    description: '',
    active: true
  });

  const [templateForm, setTemplateForm] = useState({
    id: '',
    name: '',
    start_time: '09:00',
    end_time: '17:00',
    shift_type: 'regular',
    work_location: 'office',
    duration_hours: 8,
    required_channels: [],
    isEditing: false
  });

  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({ isValid: false, errors: [] });
  const [userPasswordStrength, setUserPasswordStrength] = useState({ isValid: false, errors: [] });
  const [passwordRequirements, setPasswordRequirements] = useState(null);
  
  // System settings state
  const [systemSettings, setSystemSettings] = useState({
    on_call_includes_lunch: false,
    lunch_settings: {},
    ending_soon_hours: 1.0,
    flex_bank_default_days: 15,
    flex_bank_auto_reject: false,
    flex_bank_allowed_hours: [4, 8],
    oncall_wfh_enabled: true,
    oncall_wfh_excluded_roles: [],
    oncall_wfh_exclude_remote: true
  });
  
  // Team Flex Bank state
  const [teamFlexBank, setTeamFlexBank] = useState([]);
  const [loadingFlexBank, setLoadingFlexBank] = useState(false);
  
  // Shift types state (dynamically fetched from backend)
  const [shiftTypes, setShiftTypes] = useState([]);
  
  // Activity Types state
  const [activityTypes, setActivityTypes] = useState([]);
  const [activityTypeForm, setActivityTypeForm] = useState({
    name: '',
    value: '',
    icon: '📅',
    color: '#3B82F6',
    description: ''
  });
  const [editingActivityType, setEditingActivityType] = useState(null);
  const [showActivityTypeModal, setShowActivityTypeModal] = useState(false);
  
  // Teams state
  const [teams, setTeams] = useState([]);
  const [teamForm, setTeamForm] = useState({
    id: '',
    name: '',
    team_lead_id: '',
    manager_id: '',
    description: '',
    isEditing: false
  });
  const [showTeamModal, setShowTeamModal] = useState(false);
  const [selectedTeamForMembers, setSelectedTeamForMembers] = useState(null);
  const [showTeamMembersModal, setShowTeamMembersModal] = useState(false);
  const [selectedUserToAdd, setSelectedUserToAdd] = useState('');
  
  // Lunch & Break policy state
  const [lunchBreakPolicy, setLunchBreakPolicy] = useState(null);
  const [showBreakTypeModal, setShowBreakTypeModal] = useState(false);
  const [breakTypeForm, setBreakTypeForm] = useState({
    name: '',
    duration: 15,
    paid: true
  });
  
  // Shift type configs state (for admin management)
  const [shiftTypeConfigs, setShiftTypeConfigs] = useState([]);
  const [showShiftTypeModal, setShowShiftTypeModal] = useState(false);
  const [editingShiftType, setEditingShiftType] = useState(null);
  const [shiftTypeForm, setShiftTypeForm] = useState({
    value: '',
    display_name: '',
    description: '',
    icon: 'Clock',
    color: 'blue'
  });
  
  // PTO type configs state (for admin management)
  const [ptoTypeConfigs, setPtoTypeConfigs] = useState([]);
  const [showPtoTypeModal, setShowPtoTypeModal] = useState(false);
  const [editingPtoType, setEditingPtoType] = useState(null);
  const [ptoTypeForm, setPtoTypeForm] = useState({
    value: '',
    display_name: '',
    description: '',
    icon: 'Calendar',
    color: 'blue',
    requires_approval: true,
    requires_documentation: false,
    max_days_per_year: null
  });
  
  // Cleanup state
  const [orphanedEmployees, setOrphanedEmployees] = useState([]);
  const [loadingCleanup, setLoadingCleanup] = useState(false);
  
  // Bulk schedule delete state
  const [bulkDeleteForm, setBulkDeleteForm] = useState({
    startDate: '',
    endDate: '',
    selectedShiftTypes: [],
    previewData: null,
    isLoading: false
  });
  
  // Coverage targets state
  const [coverageTargets, setCoverageTargets] = useState([]);
  const [targetForm, setTargetForm] = useState({ 
    role: '', 
    target_count: 1,
    start_time: '00:00',
    end_time: '23:59'
  });
  const [editingTarget, setEditingTarget] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchTeams();
    fetchShiftTemplates();
    fetchRolePermissions();
    fetchAvailablePermissions();
    fetchPasswordRequirements();
    fetchCoverageTargets();
    fetchSystemSettings();
    fetchShiftTypes();
    fetchShiftTypeConfigs();
    fetchPtoTypeConfigs();
    fetchLunchBreakPolicy();
    fetchActivityTypes();
  }, []);

  // Sync systemRoles with rolePermissions from backend
  useEffect(() => {
    if (rolePermissions.length > 0) {
      console.log('Syncing systemRoles with rolePermissions:', rolePermissions.length, 'roles found');
      
      // Base roles with metadata (these have colors and descriptions)
      const baseRoles = [
        { id: 'tier1', name: 'Tier 1', description: 'Entry level support', color: '#3B82F6' },
        { id: 'tier2', name: 'Tier 2', description: 'Advanced support', color: '#8B5CF6' },
        { id: 'team_lead', name: 'Team Lead', description: 'Team leadership', color: '#10B981' },
        { id: 'solution_engineer', name: 'Solution Engineer', description: 'Solutions engineering', color: '#06B6D4' },
        { id: 'manager', name: 'Manager', description: 'Management', color: '#F59E0B' },
        { id: 'admin', name: 'Admin', description: 'System administrator', color: '#EF4444' }
      ];
      
      // Build merged roles list
      const mergedRoles = rolePermissions.map(rp => {
        const baseRole = baseRoles.find(br => br.id === rp.role);
        if (baseRole) {
          // Use base role metadata for standard roles
          return baseRole;
        } else {
          // Create role definition for custom roles
          return {
            id: rp.role,
            name: getRoleDisplayName(rp.role),
            description: getRoleDisplayName(rp.role),
            color: '#6B7280' // Gray color for custom roles
          };
        }
      });
      
      console.log('Setting systemRoles to:', mergedRoles);
      setSystemRoles(mergedRoles);
    }
  }, [rolePermissions]);

  // Add useEffect for password validation
  useEffect(() => {
    if (passwordForm.newPassword && passwordRequirements) {
      validatePasswordStrength(passwordForm.newPassword);
    }
  }, [passwordForm.newPassword, passwordRequirements]);

  // Validate user form password
  useEffect(() => {
    if (userForm.password && passwordRequirements) {
      validateUserPasswordStrength(userForm.password);
    }
  }, [userForm.password, passwordRequirements]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/users`, { headers });
      setUsers(response.data || []);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    }
  };

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/teams`, { headers });
      setTeams(response.data || []);
    } catch (error) {
      console.error('Failed to fetch teams:', error);
      toast.error('Failed to load teams');
    }
  };

  const fetchShiftTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required - please login again');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/shift-templates`, { headers });
      setShiftTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch shift templates:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired - please login again');
        window.location.href = '/login';
      } else {
        toast.error('Failed to load shift templates');
      }
    }
  };

  // fetchShifts removed - shift management handled directly in ScheduleManager

  const handleUserSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate full name is provided
      if (!userForm.name || userForm.name.trim() === '') {
        toast.error('Full Name is required');
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        setLoading(false);
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      
      if (userForm.isEditing) {
        // Update existing user with all employee information
        await axios.put(`${API}/users/${userForm.id}`, {
          username: userForm.username,
          email: userForm.email,
          role: userForm.role,
          has_schedule: userForm.has_schedule,  // Include has_schedule toggle
          work_location: userForm.work_location,  // Include work location
          // Org structure fields
          team_id: userForm.team_id || null,
          manager_id: userForm.manager_id || null,
          managed_roles: userForm.managed_roles || [],  // For team leads
          // Employee fields
          name: userForm.name,
          work_phone: userForm.work_phone,
          home_phone: userForm.home_phone,
          job_title: userForm.employeeRole,
          hire_date: userForm.hire_date || null,
          birthday: userForm.birthday || null,
          skills: userForm.skills,
          support_channels: userForm.support_channels,
          availability_start: userForm.availability_start,
          availability_end: userForm.availability_end,
          shift_preferences: userForm.shift_preferences,
          // Sidebar access
          sidebar_access: userForm.sidebar_access,
          // Compensation fields
          hourly_rate: userForm.hourly_rate,
          salary: userForm.salary,
          employment_type: userForm.employment_type,
          overtime_rate_multiplier: userForm.overtime_rate_multiplier
        }, { headers });
        toast.success('User updated successfully');
      } else {
        // Create both user account and employee record
        try {
          // First create user account
          const userResponse = await axios.post(`${API}/auth/register`, {
            username: userForm.username,
            email: userForm.email,
            role: userForm.role,
            password: userForm.password,
            managed_roles: userForm.managed_roles || []  // For team leads
          }, { headers });

          // Then create employee record
          try {
            await axios.post(`${API}/employees`, {
              name: userForm.name || userForm.username,
              email: userForm.email,
              role: userForm.employeeRole || userForm.role,
              skills: userForm.skills,
              support_channels: userForm.support_channels,
              availability_start: userForm.availability_start,
              availability_end: userForm.availability_end,
              shift_preferences: userForm.shift_preferences
            }, { headers });

            toast.success(`Employee & User created successfully! Username: ${userForm.username}`);
          } catch (employeeError) {
            console.error('Employee creation failed:', employeeError);
            toast.warning(`User account created but employee record failed: ${employeeError.response?.data?.detail || 'Unknown error'}`);
          }
        } catch (userError) {
          console.error('User creation failed:', userError);
          toast.error(`Failed to create user: ${userError.response?.data?.detail || 'Unknown error'}`);
        }
      }

      fetchUsers();
      resetUserForm();
    } catch (error) {
      console.error('Failed to save user:', error);
      const message = error.response?.data?.detail || 'Failed to save user';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to delete user "${username}"? This action cannot be undone.`)) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/users/${userId}`, { headers });
      toast.success(`User "${username}" deleted successfully`);
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      const message = error.response?.data?.detail || 'Failed to delete user';
      toast.error(message);
    }
  };

  const handleBulkDeleteTestUsers = async () => {
    const testUsers = users.filter(user => 
      user.username.toLowerCase().includes('test') || 
      user.email.toLowerCase().includes('test') ||
      user.username.toLowerCase().startsWith('demo')
    );

    if (testUsers.length === 0) {
      toast.info('No test users found to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${testUsers.length} test users? This action cannot be undone.\n\nUsers to be deleted:\n${testUsers.map(u => `- ${u.username} (${u.email})`).join('\n')}`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      let deletedCount = 0;
      let failedCount = 0;

      for (const user of testUsers) {
        try {
          await axios.delete(`${API}/users/${user.id}`, { headers });
          deletedCount++;
        } catch (error) {
          console.error(`Failed to delete user ${user.username}:`, error);
          failedCount++;
        }
      }

      if (deletedCount > 0) {
        toast.success(`Successfully deleted ${deletedCount} test users`);
      }
      if (failedCount > 0) {
        toast.error(`Failed to delete ${failedCount} users`);
      }

      fetchUsers(); // Refresh the user list
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error('Failed to delete test users');
    }
  };

  const findOrphanedEmployees = async () => {
    setLoadingCleanup(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      
      // Get all employees and users
      const [employeesResponse, usersResponse] = await Promise.all([
        axios.get(`${API}/employees`, { headers }),
        axios.get(`${API}/users`, { headers })
      ]);

      const employees = employeesResponse.data || [];
      const users = usersResponse.data || [];
      
      // Find employees without matching user accounts
      const orphaned = employees.filter(employee => 
        !users.some(user => user.email.toLowerCase() === employee.email.toLowerCase())
      );
      
      setOrphanedEmployees(orphaned);
      
      if (orphaned.length === 0) {
        toast.info('No orphaned employee records found');
      } else {
        toast.info(`Found ${orphaned.length} orphaned employee record(s)`);
      }
    } catch (error) {
      console.error('Failed to find orphaned employees:', error);
      toast.error('Failed to analyze employee data');
    } finally {
      setLoadingCleanup(false);
    }
  };

  const handleDeleteOrphanedEmployee = async (employeeId, employeeName) => {
    if (!window.confirm(`Are you sure you want to delete employee "${employeeName}"? This will also remove any schedules assigned to them. This action cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/employees/${employeeId}`, { headers });
      
      toast.success(`Employee "${employeeName}" deleted successfully`);
      
      // Refresh orphaned employees list
      findOrphanedEmployees();
    } catch (error) {
      console.error('Failed to delete employee:', error);
      const message = error.response?.data?.detail || 'Failed to delete employee';
      toast.error(message);
    }
  };

  const handleBulkDeleteOrphaned = async () => {
    if (orphanedEmployees.length === 0) {
      toast.info('No orphaned employees to delete');
      return;
    }

    const employeeList = orphanedEmployees.map(emp => `- ${emp.name} (${emp.email})`).join('\n');
    
    if (!window.confirm(`Are you sure you want to delete ${orphanedEmployees.length} orphaned employee(s)? This will remove:\n\n${employeeList}\n\nThis action cannot be undone.`)) {
      return;
    }

    try {
      setLoadingCleanup(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      let deletedCount = 0;
      let failedCount = 0;

      for (const employee of orphanedEmployees) {
        try {
          await axios.delete(`${API}/employees/${employee.id}`, { headers });
          deletedCount++;
        } catch (error) {
          console.error(`Failed to delete employee ${employee.name}:`, error);
          failedCount++;
        }
      }

      if (deletedCount > 0) {
        toast.success(`Successfully deleted ${deletedCount} orphaned employee(s)`);
      }
      if (failedCount > 0) {
        toast.error(`Failed to delete ${failedCount} employee(s)`);
      }

      // Refresh the list
      findOrphanedEmployees();
    } catch (error) {
      console.error('Bulk delete failed:', error);
      toast.error('Failed to delete orphaned employees');
    } finally {
      setLoadingCleanup(false);
    }
  };

  const handleBulkSchedulePreview = async () => {
    try {
      setBulkDeleteForm({...bulkDeleteForm, isLoading: true});
      
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const payload = {
        start_date: bulkDeleteForm.startDate || null,
        end_date: bulkDeleteForm.endDate || null,
        shift_types: bulkDeleteForm.selectedShiftTypes.length > 0 ? bulkDeleteForm.selectedShiftTypes : null,
        preview_only: true
      };

      const response = await axios.post(
        `${API}/schedules/bulk-delete-filtered`,
        payload,
        { headers }
      );

      setBulkDeleteForm({
        ...bulkDeleteForm, 
        previewData: response.data.preview,
        isLoading: false
      });

      if (response.data.count === 0) {
        toast.info('No schedules found matching the criteria');
      } else {
        toast.success(`Found ${response.data.count} schedule(s) to delete`);
      }
    } catch (error) {
      console.error('Preview failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to preview schedules');
      setBulkDeleteForm({...bulkDeleteForm, isLoading: false});
    }
  };

  const handleBulkScheduleDelete = async () => {
    if (!bulkDeleteForm.previewData || bulkDeleteForm.previewData.total_count === 0) {
      toast.error('Please preview schedules first');
      return;
    }

    const typeBreakdown = Object.entries(bulkDeleteForm.previewData.by_type)
      .map(([type, count]) => `- ${type}: ${count}`)
      .join('\n');

    if (!window.confirm(
      `Are you sure you want to delete ${bulkDeleteForm.previewData.total_count} schedule(s)?\n\nBreakdown:\n${typeBreakdown}\n\nThis action cannot be undone.`
    )) {
      return;
    }

    try {
      setBulkDeleteForm({...bulkDeleteForm, isLoading: true});
      
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const payload = {
        start_date: bulkDeleteForm.startDate || null,
        end_date: bulkDeleteForm.endDate || null,
        shift_types: bulkDeleteForm.selectedShiftTypes.length > 0 ? bulkDeleteForm.selectedShiftTypes : null,
        preview_only: false
      };

      const response = await axios.post(
        `${API}/schedules/bulk-delete-filtered`,
        payload,
        { headers }
      );

      toast.success(response.data.message);
      
      // Reset form
      setBulkDeleteForm({
        startDate: '',
        endDate: '',
        selectedShiftTypes: [],
        previewData: null,
        isLoading: false
      });
      
    } catch (error) {
      console.error('Delete failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete schedules');
      setBulkDeleteForm({...bulkDeleteForm, isLoading: false});
    }
  };

  const toggleShiftType = (shiftType) => {
    const current = bulkDeleteForm.selectedShiftTypes;
    if (current.includes(shiftType)) {
      setBulkDeleteForm({
        ...bulkDeleteForm,
        selectedShiftTypes: current.filter(t => t !== shiftType),
        previewData: null
      });
    } else {
      setBulkDeleteForm({
        ...bulkDeleteForm,
        selectedShiftTypes: [...current, shiftType],
        previewData: null
      });
    }
  };

  const handleForcePasswordReset = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to force a password reset for ${username}? They will need to change their password on next login.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.put(`${API}/auth/force-password-reset/${userId}`, {}, { headers });
      
      toast.success(`Password reset forced for ${username}. They must change their password on next login.`);
      fetchUsers(); // Refresh to show updated status
    } catch (error) {
      console.error('Failed to force password reset:', error);
      const message = error.response?.data?.detail || 'Failed to force password reset';
      toast.error(message);
    }
  };

  const handleAdminSetPassword = async (userId, username) => {
    const newPassword = window.prompt(`Enter new password for ${username}:\n\nPassword must meet the following requirements:\n- At least 12 characters\n- One uppercase letter\n- One lowercase letter\n- One number\n- One special character`);
    
    if (!newPassword) {
      return; // User cancelled
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      const response = await axios.put(
        `${API}/auth/admin-reset-password/${userId}`, 
        { new_password: newPassword }, 
        { headers }
      );
      
      toast.success(`Password updated for ${username}`);
      fetchUsers(); // Refresh user list
    } catch (error) {
      console.error('Failed to set password:', error);
      const message = error.response?.data?.detail || 'Failed to set password';
      toast.error(message);
    }
  };

  const handleToggleUserActive = async (userId, username, currentStatus) => {
    const action = currentStatus ? 'disable' : 'enable';
    if (!window.confirm(`Are you sure you want to ${action} user "${username}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.patch(`${API}/users/${userId}/toggle-active`, {}, { headers });
      
      toast.success(response.data.message);
      fetchUsers(); // Refresh to show updated status
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      const message = error.response?.data?.detail || 'Failed to toggle user status';
      toast.error(message);
    }
  };

  const handleToggleRoleActive = async (role, currentStatus) => {
    const action = currentStatus ? 'disable' : 'enable';
    if (!window.confirm(`Are you sure you want to ${action} role "${getRoleDisplayName(role)}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.patch(`${API}/role-permissions/${role}/toggle-active`, {}, { headers });
      
      toast.success(response.data.message);
      fetchRolePermissions(); // Refresh to show updated status
    } catch (error) {
      console.error('Failed to toggle role status:', error);
      const message = error.response?.data?.detail || 'Failed to toggle role status';
      toast.error(message);
    }
  };

  const fetchSystemSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/system-settings`, { headers });
      setSystemSettings(response.data);
    } catch (error) {
      console.error('Failed to fetch system settings:', error);
    }
  };

  const fetchTeamFlexBank = async () => {
    try {
      setLoadingFlexBank(true);
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/flex-bank/team`, { headers });
      setTeamFlexBank(response.data.team_balances || []);
    } catch (error) {
      console.error('Failed to fetch team flex bank:', error);
    } finally {
      setLoadingFlexBank(false);
    }
  };

  const updateUserFlexBank = async (userId, newBalance) => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/users/${userId}/flex-bank?balance=${newBalance}`, {}, { headers });
      toast.success('Flex Bank balance updated');
      fetchTeamFlexBank();
    } catch (error) {
      console.error('Failed to update flex bank:', error);
      toast.error('Failed to update Flex Bank balance');
    }
  };

  const fetchShiftTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/shift-types`, { headers });
      setShiftTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch shift types:', error);
    }
  };

  const fetchActivityTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/activity-types`, { headers });
      setActivityTypes(response.data);
    } catch (error) {
      console.error('Failed to fetch activity types:', error);
    }
  };

  const handleSaveActivityType = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      if (editingActivityType) {
        // Update existing
        await axios.put(
          `${API}/activity-types/${editingActivityType.id}`,
          activityTypeForm,
          { headers }
        );
        toast.success('Activity type updated successfully');
      } else {
        // Create new
        await axios.post(
          `${API}/activity-types`,
          activityTypeForm,
          { headers }
        );
        toast.success('Activity type created successfully');
      }
      
      fetchActivityTypes();
      setShowActivityTypeModal(false);
      setEditingActivityType(null);
      setActivityTypeForm({
        name: '',
        value: '',
        icon: '📅',
        color: '#3B82F6',
        description: ''
      });
    } catch (error) {
      console.error('Failed to save activity type:', error);
      toast.error(error.response?.data?.detail || 'Failed to save activity type');
    }
  };

  const handleDeleteActivityType = async (typeId) => {
    if (!window.confirm('Are you sure you want to delete this activity type?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.delete(`${API}/activity-types/${typeId}`, { headers });
      toast.success('Activity type deleted successfully');
      fetchActivityTypes();
    } catch (error) {
      console.error('Failed to delete activity type:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete activity type');
    }
  };

  const fetchShiftTypeConfigs = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/shift-type-configs?include_inactive=true`, { headers });
      setShiftTypeConfigs(response.data);
    } catch (error) {
      console.error('Failed to fetch shift type configs:', error);
    }
  };

  const handleCreateShiftType = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      await axios.post(
        `${API}/shift-type-configs`,
        shiftTypeForm,
        { headers }
      );
      
      toast.success('Shift type created successfully');
      setShowShiftTypeModal(false);
      resetShiftTypeForm();
      fetchShiftTypeConfigs();
      fetchShiftTypes(); // Refresh active shift types
    } catch (error) {
      console.error('Failed to create shift type:', error);
      const message = error.response?.data?.detail || 'Failed to create shift type';
      toast.error(message);
    }
  };

  const handleUpdateShiftType = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const updateData = {
        display_name: shiftTypeForm.display_name,
        description: shiftTypeForm.description,
        icon: shiftTypeForm.icon,
        color: shiftTypeForm.color
      };
      
      await axios.put(
        `${API}/shift-type-configs/${editingShiftType.id}`,
        updateData,
        { headers }
      );
      
      toast.success('Shift type updated successfully');
      setShowShiftTypeModal(false);
      setEditingShiftType(null);
      resetShiftTypeForm();
      fetchShiftTypeConfigs();
      fetchShiftTypes();
    } catch (error) {
      console.error('Failed to update shift type:', error);
      const message = error.response?.data?.detail || 'Failed to update shift type';
      toast.error(message);
    }
  };

  const handleToggleShiftTypeActive = async (shiftType) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      await axios.put(
        `${API}/shift-type-configs/${shiftType.id}`,
        { is_active: !shiftType.is_active },
        { headers }
      );
      
      toast.success(`Shift type ${shiftType.is_active ? 'disabled' : 'enabled'} successfully`);
      fetchShiftTypeConfigs();
      fetchShiftTypes();
    } catch (error) {
      console.error('Failed to toggle shift type:', error);
      const message = error.response?.data?.detail || 'Failed to toggle shift type status';
      toast.error(message);
    }
  };

  const handleDeleteShiftType = async (shiftType) => {
    if (!window.confirm(`Are you sure you want to delete "${shiftType.display_name}"? This cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.delete(
        `${API}/shift-type-configs/${shiftType.id}`,
        { headers }
      );
      
      toast.success('Shift type deleted successfully');
      fetchShiftTypeConfigs();
      fetchShiftTypes();
    } catch (error) {
      console.error('Failed to delete shift type:', error);
      const message = error.response?.data?.detail || 'Failed to delete shift type';
      toast.error(message);
    }
  };

  const openShiftTypeModal = (shiftType = null) => {
    if (shiftType) {
      setEditingShiftType(shiftType);
      setShiftTypeForm({
        value: shiftType.value,
        display_name: shiftType.display_name,
        description: shiftType.description,
        icon: shiftType.icon,
        color: shiftType.color
      });
    } else {
      setEditingShiftType(null);
      resetShiftTypeForm();
    }
    setShowShiftTypeModal(true);
  };

  const resetShiftTypeForm = () => {
    setShiftTypeForm({
      value: '',
      display_name: '',
      description: '',
      icon: 'Clock',
      color: 'blue'
    });
  };

  // PTO Type Management Functions
  const fetchPtoTypeConfigs = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('No token found for PTO type configs');
        return;
      }
      
      console.log('Fetching PTO type configs...');
      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/pto-type-configs?include_inactive=true`, { headers });
      console.log('PTO type configs response:', response.data);
      console.log('PTO type configs count:', response.data.length);
      setPtoTypeConfigs(response.data);
    } catch (error) {
      console.error('Failed to fetch PTO type configs:', error);
      console.error('Error details:', error.response?.data);
    }
  };

  const handleCreatePtoType = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      await axios.post(
        `${API}/pto-type-configs`,
        ptoTypeForm,
        { headers }
      );
      
      toast.success('PTO type created successfully');
      setShowPtoTypeModal(false);
      resetPtoTypeForm();
      fetchPtoTypeConfigs();
    } catch (error) {
      console.error('Failed to create PTO type:', error);
      const message = error.response?.data?.detail || 'Failed to create PTO type';
      toast.error(message);
    }
  };

  const handleUpdatePtoType = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      const updateData = {
        display_name: ptoTypeForm.display_name,
        description: ptoTypeForm.description,
        icon: ptoTypeForm.icon,
        color: ptoTypeForm.color,
        requires_approval: ptoTypeForm.requires_approval,
        requires_documentation: ptoTypeForm.requires_documentation,
        max_days_per_year: ptoTypeForm.max_days_per_year
      };
      
      await axios.put(
        `${API}/pto-type-configs/${editingPtoType.id}`,
        updateData,
        { headers }
      );
      
      toast.success('PTO type updated successfully');
      setShowPtoTypeModal(false);
      setEditingPtoType(null);
      resetPtoTypeForm();
      fetchPtoTypeConfigs();
    } catch (error) {
      console.error('Failed to update PTO type:', error);
      const message = error.response?.data?.detail || 'Failed to update PTO type';
      toast.error(message);
    }
  };

  const handleTogglePtoTypeActive = async (ptoType) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      await axios.put(
        `${API}/pto-type-configs/${ptoType.id}`,
        { is_active: !ptoType.is_active },
        { headers }
      );
      
      toast.success(`PTO type ${ptoType.is_active ? 'disabled' : 'enabled'} successfully`);
      fetchPtoTypeConfigs();
    } catch (error) {
      console.error('Failed to toggle PTO type:', error);
      const message = error.response?.data?.detail || 'Failed to toggle PTO type status';
      toast.error(message);
    }
  };

  const handleDeletePtoType = async (ptoType) => {
    if (!window.confirm(`Are you sure you want to delete "${ptoType.display_name}"? This cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      
      await axios.delete(
        `${API}/pto-type-configs/${ptoType.id}`,
        { headers }
      );
      
      toast.success('PTO type deleted successfully');
      fetchPtoTypeConfigs();
    } catch (error) {
      console.error('Failed to delete PTO type:', error);
      const message = error.response?.data?.detail || 'Failed to delete PTO type';
      toast.error(message);
    }
  };

  const openPtoTypeModal = (ptoType = null) => {
    if (ptoType) {
      setEditingPtoType(ptoType);
      setPtoTypeForm({
        value: ptoType.value,
        display_name: ptoType.display_name,
        description: ptoType.description,
        icon: ptoType.icon,
        color: ptoType.color,
        requires_approval: ptoType.requires_approval,
        requires_documentation: ptoType.requires_documentation,
        max_days_per_year: ptoType.max_days_per_year
      });
    } else {
      setEditingPtoType(null);
      resetPtoTypeForm();
    }
    setShowPtoTypeModal(true);
  };

  const resetPtoTypeForm = () => {
    setPtoTypeForm({
      value: '',
      display_name: '',
      description: '',
      icon: 'Calendar',
      color: 'blue',
      requires_approval: true,
      requires_documentation: false,
      max_days_per_year: null
    });
  };

  const updateSystemSettings = async (settingsData) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { 
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      await axios.put(
        `${API}/system-settings`,
        settingsData,
        { headers }
      );
      
      toast.success('System settings updated successfully');
      fetchSystemSettings();
    } catch (error) {
      console.error('Failed to update system settings:', error);
      const message = error.response?.data?.detail || 'Failed to update system settings';
      toast.error(message);
    }
  };

  const resetUserForm = () => {
    setUserForm({
      id: '',
      username: '',
      email: '',
      role: 'tier_1',
      password: '',
      isEditing: false,
      // Employee fields
      name: '',
      phone: '',
      employeeRole: '',
      skills: [],
      support_channels: [],
      availability_start: '09:00',
      availability_end: '17:00',
      shift_preferences: []
    });
  };

  const editUser = (user) => {
    setUserForm({
      id: user.id,
      username: user.username,
      email: user.email,
      role: user.role || 'tier_1',
      password: '', // Don't edit passwords, only for new users
      isEditing: true,
      has_schedule: user.has_schedule !== false,  // Default to true if not set
      work_location: user.work_location || 'office',  // Default to office if not set
      // Org structure fields
      team_id: user.team_id || '',
      manager_id: user.manager_id || '',
      managed_roles: user.managed_roles || [],  // For team leads
      // Employee fields - populate from user data
      name: user.name || '',
      work_phone: user.work_phone || '',
      home_phone: user.home_phone || '',
      employeeRole: user.job_title || '',
      hire_date: user.hire_date || '',
      birthday: user.birthday || '',
      skills: user.skills || [],
      support_channels: user.support_channels || [],
      availability_start: user.availability_start || '09:00',
      availability_end: user.availability_end || '17:00',
      shift_preferences: user.shift_preferences || [],
      // Sidebar access
      sidebar_access: user.sidebar_access || null,
      // Compensation fields
      hourly_rate: user.hourly_rate || null,
      salary: user.salary || null,
      employment_type: user.employment_type || 'hourly',
      overtime_rate_multiplier: user.overtime_rate_multiplier || 1.5
    });
  };

  const handleLunchSettingsSave = () => {
    toast.success('Lunch settings updated successfully');
    // In a real app, this would save to backend
  };

  const addChannel = () => {
    if (channelForm.name.trim()) {
      setChannels([...channels, channelForm.name.toLowerCase().trim()]);
      setChannelForm({ name: '', description: '', active: true });
      toast.success('Channel added successfully');
    }
  };

  const removeChannel = (channelToRemove) => {
    setChannels(channels.filter(channel => channel !== channelToRemove));
    toast.success('Channel removed successfully');
  };

  // Team management functions
  const handleTeamSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        setLoading(false);
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const teamData = {
        name: teamForm.name,
        team_lead_id: teamForm.team_lead_id || null,
        manager_id: teamForm.manager_id || null,
        description: teamForm.description || null
      };

      if (teamForm.isEditing) {
        await axios.put(`${API}/teams/${teamForm.id}`, teamData, { headers });
        toast.success('Team updated successfully');
      } else {
        await axios.post(`${API}/teams`, teamData, { headers });
        toast.success('Team created successfully');
      }

      fetchTeams();
      resetTeamForm();
      setShowTeamModal(false);
    } catch (error) {
      console.error('Failed to save team:', error);
      toast.error(error.response?.data?.detail || 'Failed to save team');
    } finally {
      setLoading(false);
    }
  };

  const editTeam = (team) => {
    setTeamForm({
      id: team.id,
      name: team.name,
      team_lead_id: team.team_lead_id || '',
      manager_id: team.manager_id || '',
      description: team.description || '',
      isEditing: true
    });
    setShowTeamModal(true);
  };

  const resetTeamForm = () => {
    setTeamForm({
      id: '',
      name: '',
      team_lead_id: '',
      manager_id: '',
      description: '',
      isEditing: false
    });
  };

  const handleDeleteTeam = async (teamId, teamName) => {
    if (!window.confirm(`Are you sure you want to delete team "${teamName}"?`)) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/teams/${teamId}`, { headers });
      toast.success(`Team "${teamName}" deleted successfully`);
      fetchTeams();
    } catch (error) {
      console.error('Failed to delete team:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete team');
    }
  };

  const openTeamMembersModal = (team) => {
    setSelectedTeamForMembers(team);
    setShowTeamMembersModal(true);
    setSelectedUserToAdd('');
  };

  const handleAddMemberToTeam = async () => {
    if (!selectedUserToAdd || selectedUserToAdd === 'none') {
      toast.error('Please select a user to add');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      
      // Get the full user data first
      const userToAdd = users.find(u => u.id === selectedUserToAdd);
      if (!userToAdd) {
        toast.error('User not found');
        return;
      }

      // Update the user with all their existing data plus the new team_id
      await axios.put(`${API}/users/${selectedUserToAdd}`, {
        username: userToAdd.username,
        email: userToAdd.email,
        role: userToAdd.role,
        team_id: selectedTeamForMembers.id,
        manager_id: userToAdd.manager_id || null,
        name: userToAdd.name,
        work_phone: userToAdd.work_phone,
        home_phone: userToAdd.home_phone,
        job_title: userToAdd.job_title,
        skills: userToAdd.skills || [],
        support_channels: userToAdd.support_channels || [],
        availability_start: userToAdd.availability_start || '09:00',
        availability_end: userToAdd.availability_end || '17:00',
        shift_preferences: userToAdd.shift_preferences || [],
        hourly_rate: userToAdd.hourly_rate,
        salary: userToAdd.salary,
        employment_type: userToAdd.employment_type || 'hourly',
        overtime_rate_multiplier: userToAdd.overtime_rate_multiplier || 1.5
      }, { headers });

      toast.success('Team member added successfully');
      setSelectedUserToAdd('');
      
      // Refresh users to update the UI
      await fetchUsers();
    } catch (error) {
      console.error('Failed to add team member:', error);
      toast.error(error.response?.data?.detail || 'Failed to add team member');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMemberFromTeam = async (userId, userName) => {
    if (!window.confirm(`Remove ${userName} from this team?`)) return;

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      
      // Get the full user data first
      const userToRemove = users.find(u => u.id === userId);
      if (!userToRemove) {
        toast.error('User not found');
        return;
      }

      // Update the user with all their existing data but remove team_id
      await axios.put(`${API}/users/${userId}`, {
        username: userToRemove.username,
        email: userToRemove.email,
        role: userToRemove.role,
        team_id: null,
        manager_id: userToRemove.manager_id || null,
        name: userToRemove.name,
        work_phone: userToRemove.work_phone,
        home_phone: userToRemove.home_phone,
        job_title: userToRemove.job_title,
        skills: userToRemove.skills || [],
        support_channels: userToRemove.support_channels || [],
        availability_start: userToRemove.availability_start || '09:00',
        availability_end: userToRemove.availability_end || '17:00',
        shift_preferences: userToRemove.shift_preferences || [],
        hourly_rate: userToRemove.hourly_rate,
        salary: userToRemove.salary,
        employment_type: userToRemove.employment_type || 'hourly',
        overtime_rate_multiplier: userToRemove.overtime_rate_multiplier || 1.5
      }, { headers });

      toast.success('Team member removed successfully');
      
      // Refresh users to update the UI
      await fetchUsers();
    } catch (error) {
      console.error('Failed to remove team member:', error);
      toast.error(error.response?.data?.detail || 'Failed to remove team member');
    } finally {
      setLoading(false);
    }
  };

  const fetchRolePermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required - please login again');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/role-permissions`, { headers });
      console.log('Fetched roles from API:', response.data?.length, 'roles');
      setRolePermissions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch role permissions:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired - please login again');
        window.location.href = '/login';
      } else {
        toast.error('Failed to load role permissions');
      }
    }
  };

  // Lunch & Break Policy functions
  const fetchLunchBreakPolicy = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/lunch-break-policy`, { headers });
      setLunchBreakPolicy(response.data || null);
    } catch (error) {
      console.error('Failed to fetch lunch/break policy:', error);
    }
  };

  const updateLunchBreakPolicy = async (policyData) => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/lunch-break-policy`, policyData, { headers });
      
      toast.success('Lunch & break policy updated successfully');
      await fetchLunchBreakPolicy();
    } catch (error) {
      console.error('Failed to update lunch/break policy:', error);
      toast.error(error.response?.data?.detail || 'Failed to update policy');
    } finally {
      setLoading(false);
    }
  };

  const handleAddBreakType = async () => {
    if (!breakTypeForm.name.trim()) {
      toast.error('Please enter a break type name');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/lunch-break-policy/break-types`, breakTypeForm, { headers });
      
      toast.success('Break type added successfully');
      setBreakTypeForm({ name: '', duration: 15, paid: true });
      setShowBreakTypeModal(false);
      await fetchLunchBreakPolicy();
    } catch (error) {
      console.error('Failed to add break type:', error);
      toast.error(error.response?.data?.detail || 'Failed to add break type');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteBreakType = async (breakTypeId) => {
    if (!window.confirm('Are you sure you want to delete this break type?')) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/lunch-break-policy/break-types/${breakTypeId}`, { headers });
      
      toast.success('Break type deleted successfully');
      await fetchLunchBreakPolicy();
    } catch (error) {
      console.error('Failed to delete break type:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete break type');
    }
  };

  const fetchAvailablePermissions = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required - please login again');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/permissions/available`, { headers });
      setAvailablePermissions(response.data || []);
    } catch (error) {
      console.error('Failed to fetch available permissions:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired - please login again');
        window.location.href = '/login';
      } else {
        toast.error('Failed to load available permissions');
      }
    }
  };

  const fetchPasswordRequirements = async () => {
    try {
      const response = await axios.get(`${API}/auth/password-requirements`);
      setPasswordRequirements(response.data);
    } catch (error) {
      console.error('Failed to fetch password requirements:', error);
    }
  };


  const fetchCoverageTargets = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      const response = await axios.get(`${API}/coverage-targets`, { headers });
      setCoverageTargets(response.data || []);
    } catch (error) {
      console.error('Failed to fetch coverage targets:', error);
    }
  };

  const handleSaveCoverageTarget = async () => {
    try {
      if (!targetForm.role || targetForm.target_count < 0) {
        toast.error('Please select a role and enter a valid target count');
        return;
      }
      
      // Validate time format
      const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
      if (!timeRegex.test(targetForm.start_time) || !timeRegex.test(targetForm.end_time)) {
        toast.error('Invalid time format. Use HH:MM (e.g., 08:00)');
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      
      if (editingTarget) {
        // Update existing target
        await axios.put(`${API}/coverage-targets/${editingTarget.id}`, {
          target_count: targetForm.target_count,
          start_time: targetForm.start_time,
          end_time: targetForm.end_time
        }, { headers });
        toast.success(`Coverage target updated for ${targetForm.role}`);
        setEditingTarget(null);
      } else {
        // Create new target
        await axios.post(`${API}/coverage-targets`, targetForm, { headers });
        toast.success(`Coverage target for ${targetForm.role} (${targetForm.start_time}-${targetForm.end_time}) set to ${targetForm.target_count}`);
      }
      
      setTargetForm({ role: '', target_count: 1, start_time: '00:00', end_time: '23:59' });
      fetchCoverageTargets();
    } catch (error) {
      console.error('Failed to save coverage target:', error);
      toast.error(error.response?.data?.detail || 'Failed to save coverage target');
    }
  };

  const handleEditCoverageTarget = (target) => {
    setEditingTarget(target);
    setTargetForm({
      role: target.role,
      target_count: target.target_count,
      start_time: target.start_time || '00:00',
      end_time: target.end_time || '23:59'
    });
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => {
    setEditingTarget(null);
    setTargetForm({ role: '', target_count: 1, start_time: '00:00', end_time: '23:59' });
  };

  const handleDeleteCoverageTarget = async (targetId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/coverage-targets/${targetId}`, { headers });
      
      toast.success('Coverage target deleted');
      fetchCoverageTargets();
    } catch (error) {
      console.error('Failed to delete coverage target:', error);
      toast.error('Failed to delete coverage target');
    }
  };


  const validatePasswordStrength = (password) => {
    if (!passwordRequirements) return;

    const errors = [];
    
    if (password.length < passwordRequirements.min_length) {
      errors.push(`At least ${passwordRequirements.min_length} characters`);
    }
    if (passwordRequirements.require_uppercase && !/[A-Z]/.test(password)) {
      errors.push('One uppercase letter');
    }
    if (passwordRequirements.require_lowercase && !/[a-z]/.test(password)) {
      errors.push('One lowercase letter');
    }
    if (passwordRequirements.require_numbers && !/[0-9]/.test(password)) {
      errors.push('One number');
    }
    if (passwordRequirements.require_special_chars && !new RegExp(`[${passwordRequirements.special_chars.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`).test(password)) {
      errors.push('One special character');
    }

    setPasswordStrength({
      isValid: errors.length === 0,
      errors
    });
  };

  const validateUserPasswordStrength = (password) => {
    if (!passwordRequirements) return;

    const errors = [];
    
    if (password.length < passwordRequirements.min_length) {
      errors.push(`At least ${passwordRequirements.min_length} characters`);
    }
    if (passwordRequirements.require_uppercase && !/[A-Z]/.test(password)) {
      errors.push('One uppercase letter');
    }
    if (passwordRequirements.require_lowercase && !/[a-z]/.test(password)) {
      errors.push('One lowercase letter');
    }
    if (passwordRequirements.require_numbers && !/[0-9]/.test(password)) {
      errors.push('One number');
    }
    if (passwordRequirements.require_special_chars && !new RegExp(`[${passwordRequirements.special_chars.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`).test(password)) {
      errors.push('One special character');
    }

    setUserPasswordStrength({
      isValid: errors.length === 0,
      errors
    });
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (!passwordStrength.isValid) {
      toast.error('Password does not meet requirements');
      return;
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      await axios.put(`${API}/auth/change-password`, {
        current_password: passwordForm.currentPassword,
        new_password: passwordForm.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success('Password changed successfully!');
      setPasswordForm({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setPasswordStrength({ isValid: false, errors: [] });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to change password';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const updateRolePermissions = async (role, permissions) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required - please login again');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      await axios.post(`${API}/role-permissions`, { role, permissions }, { headers });
      toast.success(`Updated permissions for ${getRoleDisplayName(role)}`);
      fetchRolePermissions();
      setEditingRole(null);
      setPermissionChanges({});
    } catch (error) {
      console.error('Failed to update role permissions:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired - please login again');
        window.location.href = '/login';
      } else {
        toast.error('Failed to update permissions');
      }
    }
  };

  const handleCreateRole = async () => {
    try {
      // Validation
      if (!roleForm.id || !roleForm.name) {
        toast.error('Role ID and Name are required');
        return;
      }

      // Check minimum permissions
      const requiredPermissions = ['view_dashboard', 'view_schedules'];
      const hasMinimumPermissions = requiredPermissions.every(p => roleForm.permissions.includes(p));
      
      if (!hasMinimumPermissions) {
        toast.error('Roles must have at least "View Dashboard" and "View Schedules" permissions');
        return;
      }

      // Check if role already exists
      if (rolePermissions.find(r => r.role === roleForm.id)) {
        toast.error('Role ID already exists');
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required - please login again');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      
      console.log('Creating role:', roleForm.id, 'with permissions:', roleForm.permissions);
      
      // Create role with permissions in one step
      await axios.post(`${API}/role-permissions`, { 
        role: roleForm.id, 
        permissions: roleForm.permissions 
      }, { headers });

      toast.success(`Role "${roleForm.name}" created successfully with ${roleForm.permissions.length} permissions`);
      
      // Reset form and close modal
      setRoleForm({ 
        id: '', 
        name: '', 
        description: '', 
        color: '#3B82F6',
        permissions: ['view_dashboard', 'view_schedules', 'view_pto', 'request_own_pto']
      });
      setShowRoleModal(false);
      
      // Reload roles list after a short delay to ensure backend has processed
      setTimeout(() => {
        console.log('Refreshing roles list...');
        fetchRolePermissions();
      }, 500);
    } catch (error) {
      console.error('Failed to create role - Full error:', error);
      console.error('Error response:', error.response?.data);
      const errorMsg = error.response?.data?.detail || error.message || 'Failed to create role';
      toast.error(errorMsg);
    }
  };

  const toggleRolePermission = (permission, checked) => {
    const currentPermissions = roleForm.permissions || [];
    
    // Use the checked parameter if provided, otherwise toggle
    const shouldAdd = checked !== undefined ? checked : !currentPermissions.includes(permission);
    
    if (!shouldAdd) {
      // Removing permission
      // Don't allow removing required permissions
      if (['view_dashboard', 'view_schedules'].includes(permission)) {
        toast.error('This is a required permission and cannot be removed');
        return;
      }
      setRoleForm({
        ...roleForm,
        permissions: currentPermissions.filter(p => p !== permission)
      });
    } else {
      // Adding permission
      if (!currentPermissions.includes(permission)) {
        setRoleForm({
          ...roleForm,
          permissions: [...currentPermissions, permission]
        });
      }
    }
  };

  const handleCreateTemplate = async () => {
    try {
      if (!templateForm.name || !templateForm.start_time || !templateForm.end_time) {
        toast.error('Please fill in all required fields');
        return;
      }
      
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required - please login again');
        return;
      }
      
      const headers = { Authorization: `Bearer ${token}` };
      
      // Calculate duration
      const start = new Date(`2000-01-01T${templateForm.start_time}:00`);
      const end = new Date(`2000-01-01T${templateForm.end_time}:00`);
      let duration = (end - start) / (1000 * 60 * 60);
      if (duration < 0) duration += 24; // Handle overnight shifts
      
      const templateData = {
        name: templateForm.name,
        start_time: templateForm.start_time,
        end_time: templateForm.end_time,
        shift_type: templateForm.shift_type || 'regular',
        work_location: templateForm.work_location || 'office',
        duration_hours: duration,
        required_channels: templateForm.required_channels || [],
        min_employees: 1,
        max_employees: 5
      };
      
      if (templateForm.isEditing && templateForm.id) {
        // Update existing template
        await axios.put(`${API}/shift-templates/${templateForm.id}`, templateData, { headers });
        toast.success('Shift template updated successfully');
      } else {
        // Create new template
        await axios.post(`${API}/shift-templates`, templateData, { headers });
        toast.success('Shift template created successfully');
      }
      
      setShowTemplateModal(false);
      resetTemplateForm();
      fetchShiftTemplates();
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const handleEditTemplate = (template) => {
    setTemplateForm({
      id: template.id,
      name: template.name,
      start_time: template.start_time,
      end_time: template.end_time,
      shift_type: template.shift_type,
      work_location: template.work_location || 'office',
      duration_hours: template.duration_hours,
      required_channels: template.required_channels || [],
      isEditing: true
    });
    setShowTemplateModal(true);
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template? This cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('Authentication required');
        return;
      }

      const headers = { Authorization: `Bearer ${token}` };
      await axios.delete(`${API}/shift-templates/${templateId}`, { headers });
      
      toast.success('Template deleted successfully');
      fetchShiftTemplates();
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const resetTemplateForm = () => {
    setTemplateForm({
      id: '',
      name: '',
      start_time: '09:00',
      end_time: '17:00',
      shift_type: 'regular',
      work_location: 'office',
      duration_hours: 8,
      required_channels: [],
      isEditing: false
    });
  };

  const getRoleDisplayName = (role) => {
    const roleNames = {
      'tier1': 'Tier 1',
      'tier2': 'Tier 2',
      'tier_1': 'Tier 1 Support',  // Legacy support
      'tier_2': 'Tier 2 Support',  // Legacy support
      'team_lead': 'Team Lead',
      'solution_engineer': 'Solution Engineer',
      'manager': 'Manager',
      'admin': 'Admin'
    };
    // Return the display name, or capitalize the role name if not found
    return roleNames[role] || role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getChannelIcon = (channel) => {
    switch(channel) {
      case 'chat': return <MessageSquare className="h-4 w-4" />;
      case 'phone': return <Phone className="h-4 w-4" />;
      case 'email': return <Mail className="h-4 w-4" />;
      default: return <AlertCircle className="h-4 w-4" />;
    }
  };

  // Helper function to get icon for shift type
  const getShiftTypeIcon = (shiftTypeValue) => {
    const iconMap = {
      'regular': Clock,
      'on_call': Phone,
      'pto': Calendar,
      'emergency': AlertCircle,
      'training': Users
    };
    return iconMap[shiftTypeValue] || Clock;
  };

  // Helper function to get color scheme for shift type
  const getShiftTypeColor = (shiftTypeValue) => {
    const colorMap = {
      'regular': { bg: 'from-blue-50 to-blue-100', border: 'border-blue-200', accent: 'bg-blue-600', text: 'text-blue-600' },
      'on_call': { bg: 'from-orange-50 to-orange-100', border: 'border-orange-200', accent: 'bg-orange-600', text: 'text-orange-600' },
      'pto': { bg: 'from-purple-50 to-purple-100', border: 'border-purple-200', accent: 'bg-purple-600', text: 'text-purple-600' },
      'emergency': { bg: 'from-red-50 to-red-100', border: 'border-red-200', accent: 'bg-red-600', text: 'text-red-600' },
      'training': { bg: 'from-green-50 to-green-100', border: 'border-green-200', accent: 'bg-green-600', text: 'text-green-600' }
    };
    return colorMap[shiftTypeValue] || { bg: 'from-gray-50 to-gray-100', border: 'border-gray-200', accent: 'bg-gray-600', text: 'text-gray-600' };
  };

  // Helper function to update lunch settings for a specific shift type
  const updateShiftTypeLunchSetting = (shiftTypeValue, field, value) => {
    const newSettings = {
      ...systemSettings,
      lunch_settings: {
        ...systemSettings.lunch_settings,
        [shiftTypeValue]: {
          ...systemSettings.lunch_settings?.[shiftTypeValue],
          [field]: value
        }
      }
    };
    setSystemSettings(newSettings);
  };

  // Helper function to get icon component for PTO types
  const getPtoTypeIcon = (iconName) => {
    const iconMap = {
      'Calendar': Calendar,
      'Plane': Plane,
      'Heart': Heart,
      'User': User,
      'Users': Users,
      'Home': Home,
      'Shield': Shield,
      'AlertCircle': AlertCircle
    };
    return iconMap[iconName] || Calendar;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Admin Settings</h1>
          <p className="text-gray-600 mt-1">
            Manage users, roles, channels, and system settings
          </p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex flex-wrap gap-1 bg-gray-100 p-1 rounded-lg" data-testid="admin-tab-strip">
        {[
          { id: 'users', label: 'Employee & User Management', icon: Users },
          { id: 'teams', label: 'Teams', icon: Building },
          { id: 'roles', label: 'Roles', icon: Shield },
          { id: 'staffing', label: 'Staffing Rules', icon: LayoutGrid },
          { id: 'holidays', label: 'Company Holidays', icon: Calendar },
          { id: 'cleanup', label: 'Data Cleanup', icon: Trash2 },
          { id: 'permissions', label: 'Role Permissions', icon: Shield },
          { id: 'templates', label: 'Shift Templates', icon: Clock },
          { id: 'channels', label: 'Support Channels', icon: MessageSquare },
          { id: 'shifttypes', label: 'Shift Types', icon: Settings },
          { id: 'ptotypes', label: 'PTO Types', icon: Calendar },
          { id: 'blackouts', label: 'PTO Blackouts', icon: CalendarOff },
          { id: 'activitytypes', label: 'Activity Types', icon: Activity },
          { id: 'lunch', label: 'Lunch & Breaks', icon: Clock },
          { id: 'flexbank', label: 'Flex Bank', icon: Home },
          { id: 'integrations', label: 'Integrations', icon: MessageSquare },
          { id: 'settings', label: 'Settings', icon: Settings },
          { id: 'feedback', label: 'User Feedback', icon: MessageSquare }
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === id
                ? 'bg-white text-blue-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Icon className="h-4 w-4" />
            <span>{label}</span>
          </button>
        ))}
      </div>

      {/* User Management Tab */}
      {activeTab === 'users' && (
        <div className="space-y-6">
          {/* Quick-action: open the modal-with-tabs to invite a new user */}
          <Card className="shadow-lg border-0">
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="min-w-0">
                <p className="font-medium text-gray-900 flex items-center gap-2">
                  <UserPlus className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  Add a new team member
                </p>
                <p className="text-xs text-gray-500 mt-0.5">We'll email them a link to set their own password — you never have to share one.</p>
              </div>
              <Button onClick={openCreateUserDialog} data-testid="open-add-user-dialog" className="w-full sm:w-auto flex-shrink-0">
                <UserPlus className="h-4 w-4 mr-2" /> Invite User
              </Button>
            </CardContent>
          </Card>

          {/* Users List */}
          <Card className="shadow-lg border-0">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center space-x-2">
                  <Users className="h-5 w-5 text-blue-600" />
                  <span>Current Users</span>
                </CardTitle>
                <div className="flex items-center space-x-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    onClick={handleBulkDeleteTestUsers}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Clean Test Users
                  </Button>
                  <span className="text-sm text-gray-500">{users.length} total</span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-[calc(100vh-28rem)] min-h-[400px] overflow-y-auto">
                {users
                  .sort((a, b) => (a.name || a.username || '').localeCompare(b.name || b.username || ''))
                  .map((user) => (
                  <div key={user.id} className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 p-4 bg-gray-50 rounded-lg" data-testid={`user-row-${user.id}`}>
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${user.is_active !== false ? 'bg-blue-100' : 'bg-gray-300'}`}>
                        <span className={`font-semibold text-sm ${user.is_active !== false ? 'text-blue-600' : 'text-gray-500'}`}>
                          {user.username?.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center space-x-2">
                          <p className={`font-medium ${user.is_active !== false ? 'text-gray-900' : 'text-gray-500'}`}>{user.username}</p>
                          {user.is_active === false && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                              Inactive
                            </span>
                          )}
                          {user.role === 'admin' && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              <Shield className="h-3 w-3 mr-1" />
                              Admin
                            </span>
                          )}
                          {user.work_location === 'remote' && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              🏠 Remote
                            </span>
                          )}
                          {user.requires_password_change && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                              <AlertCircle className="h-3 w-3 mr-1" />
                              Password Reset Required
                            </span>
                          )}
                          {user.is_temporary_password && !user.requires_password_change && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                              <Clock className="h-3 w-3 mr-1" />
                              Temporary Password
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600">{user.email}</p>
                        <p className="text-xs text-gray-500">{getRoleDisplayName(user.role)}</p>
                        {user.last_login && (
                          <p className="text-xs text-gray-400 mt-1">
                            Last login: {(() => {
                              let ts = user.last_login;
                              if (typeof ts === 'string' && !ts.endsWith('Z') && !ts.includes('+')) {
                                ts = ts + 'Z';
                              }
                              return new Date(ts).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              });
                            })()}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3 md:flex-shrink-0">
                      <div className="flex flex-col items-center space-y-1">
                        <Label htmlFor={`user-active-${user.id}`} className="text-xs text-gray-600">
                          {user.is_active !== false ? 'Active' : 'Inactive'}
                        </Label>
                        <Switch
                          id={`user-active-${user.id}`}
                          checked={user.is_active !== false}
                          onCheckedChange={() => handleToggleUserActive(user.id, user.username, user.is_active !== false)}
                          disabled={user.role === 'admin'}
                        />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button size="sm" variant="outline" onClick={() => openEditUserDialog(user)} title="Edit User" data-testid={`edit-user-${user.id}`}>
                          <Edit className="h-4 w-4 sm:mr-1" />
                          <span className="hidden sm:inline">Edit</span>
                        </Button>
                        {(user.is_temporary_password || user.requires_password_change) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={async () => {
                              try {
                                const res = await axios.post(`${API}/users/${user.id}/resend-invite`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
                                if (res.data.email_sent) toast.success(`Invite email resent to ${user.email}.`);
                                else toast.warning('Email delivery failed — copy the link from the user form.');
                              } catch (e) {
                                toast.error(e.response?.data?.detail || 'Could not resend invite.');
                              }
                            }}
                            title="Resend invite email"
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            data-testid={`resend-invite-${user.id}`}
                          >
                            <Mail className="h-4 w-4 sm:mr-1" />
                            <span className="hidden sm:inline">Resend Invite</span>
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleAdminSetPassword(user.id, user.username)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Set New Password"
                        >
                          <Key className="h-4 w-4 sm:mr-1" />
                          <span className="hidden lg:inline">Set Password</span>
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleForcePasswordReset(user.id, user.username)}
                          className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                          title="Force Password Reset on Next Login"
                        >
                          <Key className="h-4 w-4 sm:mr-1" />
                          <span className="hidden lg:inline">Force Reset</span>
                        </Button>
                        <Button size="sm" variant="destructive" onClick={() => handleDeleteUser(user.id, user.username)} title="Delete User">
                          <Trash2 className="h-4 w-4 sm:mr-1" />
                          <span className="hidden sm:inline">Delete</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
                {users.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No users found</p>
                    <p className="text-sm">Create your first user to get started</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Teams Management Tab */}
      {activeTab === 'teams' && (
        <div className="grid grid-cols-1 gap-6">
          <Card className="shadow-lg border-0">
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center space-x-2">
                  <Building className="h-5 w-5 text-blue-600" />
                  <span>Manage Teams</span>
                </CardTitle>
                <CardDescription>Create and manage organizational teams</CardDescription>
              </div>
              <Button onClick={() => { resetTeamForm(); setShowTeamModal(true); }}>
                <Plus className="h-4 w-4 mr-2" />
                Add Team
              </Button>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {teams.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Building className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No teams created yet</p>
                    <p className="text-sm">Click "Add Team" to create your first team</p>
                  </div>
                ) : (
                  <div className="grid gap-4">
                    {teams.map((team) => {
                      const teamLead = users.find(u => u.id === team.team_lead_id);
                      const manager = users.find(u => u.id === team.manager_id);
                      const memberCount = users.filter(u => u.team_id === team.id).length;

                      return (
                        <div key={team.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h3 className="text-lg font-semibold text-gray-900">{team.name}</h3>
                              {team.description && (
                                <p className="text-sm text-gray-600 mt-1">{team.description}</p>
                              )}
                              <div className="mt-3 space-y-2">
                                <div className="flex items-center text-sm">
                                  <Users className="h-4 w-4 mr-2 text-blue-600" />
                                  <span className="text-gray-600">Members:</span>
                                  <Badge variant="secondary" className="ml-2">{memberCount}</Badge>
                                  <Button
                                    variant="link"
                                    size="sm"
                                    onClick={() => openTeamMembersModal(team)}
                                    className="ml-2 text-blue-600 h-auto p-0"
                                  >
                                    Manage Members
                                  </Button>
                                </div>
                                {manager && (
                                  <div className="flex items-center text-sm">
                                    <Shield className="h-4 w-4 mr-2 text-orange-600" />
                                    <span className="text-gray-600">Manager:</span>
                                    <span className="ml-2 font-medium">{manager.name || manager.username}</span>
                                  </div>
                                )}
                                {teamLead && (
                                  <div className="flex items-center text-sm">
                                    <User className="h-4 w-4 mr-2 text-green-600" />
                                    <span className="text-gray-600">Team Lead:</span>
                                    <span className="ml-2 font-medium">{teamLead.name || teamLead.username}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex space-x-2 ml-4">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => editTeam(team)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                onClick={() => handleDeleteTeam(team.id, team.name)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Team Modal */}
      <Dialog open={showTeamModal} onOpenChange={setShowTeamModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{teamForm.isEditing ? 'Edit Team' : 'Create New Team'}</DialogTitle>
            <DialogDescription>
              {teamForm.isEditing ? 'Update team information' : 'Add a new team to your organization'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTeamSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="team-name">Team Name *</Label>
              <Input
                id="team-name"
                value={teamForm.name}
                onChange={(e) => setTeamForm({...teamForm, name: e.target.value})}
                placeholder="e.g., Support Team Alpha"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="team-description">Description</Label>
              <Input
                id="team-description"
                value={teamForm.description}
                onChange={(e) => setTeamForm({...teamForm, description: e.target.value})}
                placeholder="Brief description of the team"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="team-manager">Manager</Label>
              <Select
                value={teamForm.manager_id || "none"}
                onValueChange={(value) => setTeamForm({...teamForm, manager_id: value === "none" ? "" : value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select manager (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {users.filter(u => u.role === 'manager' || u.role === 'admin').map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name || user.username} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="team-lead">Team Lead</Label>
              <Select
                value={teamForm.team_lead_id || "none"}
                onValueChange={(value) => setTeamForm({...teamForm, team_lead_id: value === "none" ? "" : value})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select team lead (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {users.filter(u => u.role === 'team_lead' || u.role === 'manager' || u.role === 'admin').map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      {user.name || user.username} ({user.email})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowTeamModal(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={loading}>
                {loading ? 'Saving...' : teamForm.isEditing ? 'Update Team' : 'Create Team'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Team Members Management Modal */}
      <Dialog open={showTeamMembersModal} onOpenChange={setShowTeamMembersModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Manage Team Members - {selectedTeamForMembers?.name}</DialogTitle>
            <DialogDescription>
              Add or remove members from this team
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Add Member Section */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <h4 className="font-medium text-sm mb-3">Add New Member</h4>
              <div className="flex gap-2">
                <Select
                  value={selectedUserToAdd || "none"}
                  onValueChange={(value) => setSelectedUserToAdd(value === "none" ? "" : value)}
                >
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select a user to add" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select a user...</SelectItem>
                    {users
                      .filter(u => !u.team_id || u.team_id !== selectedTeamForMembers?.id)
                      .map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.name || user.username} ({user.email}) - {user.role}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <Button onClick={handleAddMemberToTeam} disabled={!selectedUserToAdd || selectedUserToAdd === 'none'}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>

            {/* Current Members List */}
            <div>
              <h4 className="font-medium text-sm mb-3">Current Members ({users.filter(u => u.team_id === selectedTeamForMembers?.id).length})</h4>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {users
                  .filter(u => u.team_id === selectedTeamForMembers?.id)
                  .length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No members in this team yet</p>
                  </div>
                ) : (
                  users
                    .filter(u => u.team_id === selectedTeamForMembers?.id)
                    .map(member => (
                      <div
                        key={member.id}
                        className="flex items-center justify-between p-3 bg-white border rounded-lg hover:shadow-sm transition-shadow"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-semibold text-xs">
                              {(member.name || member.username).charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-sm">{member.name || member.username}</p>
                            <p className="text-xs text-gray-500">{member.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Badge variant="outline" className="text-xs">
                            {member.role}
                          </Badge>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveMemberFromTeam(member.id, member.name || member.username)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTeamMembersModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal-with-tabs for inviting/editing users */}
      <UserFormDialog
        open={userDialogOpen}
        onOpenChange={setUserDialogOpen}
        initial={userDialogInitial}
        systemRoles={systemRoles}
        teams={teams}
        users={users}
        onSaved={handleUserDialogSaved}
      />

      {/* Roles Management Tab */}
      {activeTab === 'roles' && (
        <Card className="shadow-lg border-0">
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Shield className="h-5 w-5 text-blue-600" />
              <span>Manage Roles</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Create Role Button */}
            <div className="flex justify-between items-center mb-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Existing Roles</h3>
                <p className="text-sm text-gray-600">Click on a role to configure its permissions</p>
              </div>
              <Dialog open={showRoleModal} onOpenChange={(open) => {
                setShowRoleModal(open);
                if (open) {
                  // Reset form with default permissions when opening
                  setRoleForm({
                    id: '',
                    name: '',
                    description: '',
                    color: '#3B82F6',
                    permissions: ['view_dashboard', 'view_schedules', 'view_pto', 'request_own_pto']
                  });
                }
              }}>
                <DialogTrigger asChild>
                  <Button className="bg-purple-600 hover:bg-purple-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Create New Role
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Create New Role</DialogTitle>
                    <DialogDescription>
                      Define the role and assign permissions. Minimum required: View Dashboard and View Schedules.
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-6">
                    {/* Role Basic Info */}
                    <div className="space-y-4">
                      <h4 className="font-medium text-gray-900">Role Information</h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="modal-role-id">Role ID *</Label>
                          <Input
                            id="modal-role-id"
                            placeholder="e.g., tier3, supervisor"
                            value={roleForm.id}
                            onChange={(e) => setRoleForm({...roleForm, id: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                          />
                          <p className="text-xs text-gray-500 mt-1">Lowercase with underscores</p>
                        </div>
                        <div>
                          <Label htmlFor="modal-role-name">Display Name *</Label>
                          <Input
                            id="modal-role-name"
                            placeholder="e.g., Tier 3 Support"
                            value={roleForm.name}
                            onChange={(e) => setRoleForm({...roleForm, name: e.target.value})}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Permissions Selection */}
                    <div className="space-y-4 border-t pt-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-gray-900">Permissions</h4>
                        <Badge variant="outline" className="bg-blue-50">
                          {roleForm.permissions?.length || 0} selected
                        </Badge>
                      </div>
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3 text-sm text-yellow-800">
                        <strong>Required:</strong> All roles must have "View Dashboard" and "View Schedules" permissions (pre-selected and cannot be removed).
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-h-96 overflow-y-auto p-2" key={roleForm.permissions?.join(',') || 'empty'}>
                        {availablePermissions.map((group) => (
                          <div key={group.group} className="space-y-3">
                            <h5 className="font-medium text-sm text-gray-700 border-b pb-1">
                              {group.group}
                            </h5>
                            <div className="space-y-2">
                              {group.permissions.map((perm) => {
                                // Backend returns 'permission' field, not 'value'
                                const permValue = perm.permission || perm.value;
                                const isRequired = ['view_dashboard', 'view_schedules'].includes(permValue);
                                const isSelected = roleForm.permissions?.includes(permValue);
                                
                                return (
                                  <div key={permValue} className="flex items-start space-x-2">
                                    <Checkbox
                                      id={`create-${permValue}`}
                                      checked={isSelected}
                                      onCheckedChange={(checked) => toggleRolePermission(permValue, checked)}
                                      disabled={isRequired}
                                      className={isRequired ? 'opacity-50 cursor-not-allowed' : ''}
                                    />
                                    <div className="flex-1">
                                      <Label 
                                        htmlFor={`create-${permValue}`}
                                        className={`text-sm cursor-pointer ${isRequired ? 'font-medium text-blue-700' : ''}`}
                                      >
                                        {perm.label || perm.description}
                                        {isRequired && <span className="ml-1 text-xs">(Required)</span>}
                                      </Label>
                                      <p className="text-xs text-gray-500">{perm.description}</p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowRoleModal(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleCreateRole} className="bg-purple-600 hover:bg-purple-700">
                      Create Role with {roleForm.permissions?.length || 0} Permissions
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid grid-cols-1 gap-6">
              {/* Roles List */}
              <Card className="xl:col-span-2 border border-gray-200">
                <CardHeader className="bg-gray-50">
                  <CardTitle className="text-base">Current Roles</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {systemRoles.map(role => {
                      const userCount = users.filter(u => u.role === role.id).length;
                      const isProtected = role.id === 'admin';
                      
                      return (
                        <div
                          key={role.id}
                          className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50"
                        >
                          <div className="flex items-center space-x-3 flex-1">
                            <div
                              className="w-4 h-4 rounded"
                              style={{ backgroundColor: role.color }}
                            />
                            <div className="flex-1">
                              <div className="flex items-center space-x-2">
                                <span className="font-medium">{role.name}</span>
                                <span className="text-xs text-gray-500">({role.id})</span>
                                {isProtected && (
                                  <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">
                                    Protected
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">{role.description}</p>
                              <p className="text-xs text-gray-500 mt-1">{userCount} user(s) with this role</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => {
                                setRoleForm(role);
                                setEditingRoleDefinition(role);
                              }}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            {!isProtected && userCount === 0 && (
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => {
                                  if (window.confirm(`Delete role "${role.name}"?`)) {
                                    setSystemRoles(systemRoles.filter(r => r.id !== role.id));
                                    toast.success('Role deleted');
                                  }
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {systemRoles.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                      <Shield className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No roles found</p>
                      <p className="text-sm">Add roles to organize your team</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Info Box */}
            <Card className="mt-6 bg-blue-50 border-blue-200">
              <CardContent className="p-4">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Role Management Tips:</p>
                    <ul className="list-disc list-inside space-y-1 text-xs">
                      <li>Role IDs cannot be changed after creation (they're used in the database)</li>
                      <li>Protected roles (like Admin) cannot be deleted</li>
                      <li>You can only delete roles that have no users assigned</li>
                      <li>Role colors help visually identify teams in the schedule</li>
                      <li>Changes are stored locally - refresh persists them</li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>
          </CardContent>
        </Card>
      )}





      {/* Company Holidays Tab */}
      {activeTab === 'holidays' && (
        <Holidays />
      )}

      {/* PTO Blackouts Tab */}
      {activeTab === 'blackouts' && (
        <PTOBlackouts />
      )}

      {/* Staffing Rules Tab */}
      {activeTab === 'staffing' && (
        <StaffingRulesAdmin />
      )}

      {/* Data Cleanup Tab */}
      {activeTab === 'cleanup' && (
        <div className="space-y-6">
          <Card className="shadow-lg border-0">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Trash2 className="h-5 w-5 text-red-600" />
                <span>Data Cleanup Tools</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-6">
                Clean up orphaned employee records that don't have corresponding user accounts. 
                This helps maintain data consistency between employees and users.
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Orphaned Employees Scanner */}
                <Card className="border border-orange-200">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center space-x-2">
                      <AlertCircle className="h-5 w-5 text-orange-600" />
                      <span>Orphaned Employee Records</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Find employee records that don't have matching user accounts. 
                      These records may have been left behind when users were deleted.
                    </p>
                    
                    <Button 
                      onClick={findOrphanedEmployees}
                      disabled={loadingCleanup}
                      className="w-full"
                    >
                      {loadingCleanup ? 'Scanning...' : 'Scan for Orphaned Records'}
                    </Button>

                    {orphanedEmployees.length > 0 && (
                      <div className="mt-4 space-y-3">
                        <div className="flex items-center justify-between">
                          <h4 className="font-medium text-gray-900">
                            Found {orphanedEmployees.length} orphaned record(s)
                          </h4>
                          <Button 
                            size="sm"
                            variant="destructive"
                            onClick={handleBulkDeleteOrphaned}
                            disabled={loadingCleanup}
                          >
                            Delete All
                          </Button>
                        </div>
                        
                        <div className="max-h-64 overflow-y-auto space-y-2">
                          {orphanedEmployees.map((employee) => (
                            <div key={employee.id} className="flex items-center justify-between p-3 bg-red-50 border border-red-200 rounded-lg">
                              <div>
                                <p className="font-medium text-gray-900">{employee.name}</p>
                                <p className="text-sm text-gray-600">{employee.email}</p>
                                <p className="text-xs text-gray-500">Role: {employee.role}</p>
                              </div>
                              <Button 
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDeleteOrphanedEmployee(employee.id, employee.name)}
                              >
                                <Trash2 className="h-4 w-4 mr-1" />
                                Delete
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Bulk Schedule Deletion */}
                <Card className="border border-red-200">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center space-x-2">
                      <Calendar className="h-5 w-5 text-red-600" />
                      <span>Bulk Schedule Deletion</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <p className="text-sm text-gray-600">
                      Delete multiple schedules at once by date range and shift type. Preview before deleting.
                    </p>

                    {/* Date Range */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Date Range</Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Input
                            type="date"
                            value={bulkDeleteForm.startDate}
                            onChange={(e) => setBulkDeleteForm({...bulkDeleteForm, startDate: e.target.value, previewData: null})}
                            placeholder="Start Date"
                          />
                        </div>
                        <div>
                          <Input
                            type="date"
                            value={bulkDeleteForm.endDate}
                            onChange={(e) => setBulkDeleteForm({...bulkDeleteForm, endDate: e.target.value, previewData: null})}
                            placeholder="End Date"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500">Leave empty to select all dates</p>
                    </div>

                    {/* Shift Type Selection */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Shift Types to Delete</Label>
                      <div className="grid grid-cols-2 gap-2">
                        {shiftTypes.map((shiftType) => (
                          <div key={shiftType.value} className="flex items-center space-x-2">
                            <Checkbox
                              id={`bulk-${shiftType.value}`}
                              checked={bulkDeleteForm.selectedShiftTypes.includes(shiftType.value)}
                              onCheckedChange={() => toggleShiftType(shiftType.value)}
                            />
                            <Label 
                              htmlFor={`bulk-${shiftType.value}`}
                              className="text-sm cursor-pointer"
                            >
                              {shiftType.display_name}
                            </Label>
                          </div>
                        ))}
                      </div>
                      <p className="text-xs text-gray-500">Leave unchecked to select all types</p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex space-x-2">
                      <Button
                        onClick={handleBulkSchedulePreview}
                        disabled={bulkDeleteForm.isLoading}
                        variant="outline"
                        className="flex-1"
                      >
                        <Eye className="h-4 w-4 mr-2" />
                        {bulkDeleteForm.isLoading ? 'Loading...' : 'Preview'}
                      </Button>
                      <Button
                        onClick={handleBulkScheduleDelete}
                        disabled={!bulkDeleteForm.previewData || bulkDeleteForm.isLoading}
                        variant="destructive"
                        className="flex-1"
                      >
                        <Trash2 className="h-4 w-4 mr-2" />
                        Delete
                      </Button>
                    </div>

                    {/* Preview Results */}
                    {bulkDeleteForm.previewData && (
                      <div className="p-4 bg-red-50 border border-red-200 rounded-lg space-y-2">
                        <h4 className="font-semibold text-red-900">
                          Preview: {bulkDeleteForm.previewData.total_count} schedule(s) will be deleted
                        </h4>
                        <div className="text-sm text-red-800 space-y-1">
                          <p><strong>Date Range:</strong> {bulkDeleteForm.previewData.date_range.start} to {bulkDeleteForm.previewData.date_range.end}</p>
                          <p><strong>By Type:</strong></p>
                          <ul className="ml-4 space-y-1">
                            {Object.entries(bulkDeleteForm.previewData.by_type).map(([type, count]) => (
                              <li key={type}>• {type}: {count} schedule(s)</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Cleanup Guidelines */}
                <Card className="border border-blue-200">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center space-x-2">
                      <AlertCircle className="h-5 w-5 text-blue-600" />
                      <span>Cleanup Guidelines</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">What are orphaned records?</h4>
                        <p className="text-sm text-gray-600">
                          Employee records that exist in the system but don't have corresponding user accounts. 
                          This typically happens when user accounts are deleted but employee records remain.
                        </p>
                      </div>

                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">When to clean up?</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li>• After removing user accounts</li>
                          <li>• During regular maintenance</li>
                          <li>• When schedule assignments fail</li>
                          <li>• Before system migrations</li>
                        </ul>
                      </div>

                      <div>
                        <h4 className="font-medium text-gray-900 mb-2">What gets deleted?</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          <li>• Employee profile information</li>
                          <li>• Associated schedule assignments</li>
                          <li>• Skill and availability data</li>
                          <li>• Historical shift records</li>
                        </ul>
                      </div>

                      <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <p className="text-sm text-yellow-800">
                          <AlertCircle className="h-4 w-4 inline mr-1" />
                          <strong>Warning:</strong> Deletion is permanent and cannot be undone. 
                          Always verify records before deletion.
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Role Permissions Tab */}
      {activeTab === 'permissions' && (
        <div className="space-y-6">
          <Card className="shadow-lg border-0">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Shield className="h-5 w-5 text-purple-600" />
                <span>Role Permissions Management</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600 mb-6">
                Configure which functions each role can access. Changes take effect immediately for all users with that role.
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
                {rolePermissions.map((roleData) => {
                  const role = roleData.role;
                  const rolePerms = roleData;
                  const isEditing = editingRole === role;
                  const currentPermissions = isEditing 
                    ? (permissionChanges[role] || rolePerms.permissions) 
                    : rolePerms.permissions;
                  
                  console.log('Rendering role:', role, 'Display name:', getRoleDisplayName(role));

                  return (
                    <Card key={role} className={`border ${roleData.is_active !== false ? 'border-purple-200' : 'border-gray-300 opacity-60'}`}>
                      <CardHeader className="pb-3">
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <span className="text-lg font-semibold">{getRoleDisplayName(role)}</span>
                              {roleData.is_active === false && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                                  Inactive
                                </span>
                              )}
                            </div>
                            {!isEditing ? (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => {
                                  setEditingRole(role);
                                  setPermissionChanges({ ...permissionChanges, [role]: rolePerms.permissions });
                                }}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            ) : (
                              <div className="flex space-x-2">
                                <Button 
                                  size="sm" 
                                  onClick={() => updateRolePermissions(role, permissionChanges[role] || [])}
                                >
                                  Save
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => {
                                    setEditingRole(null);
                                    setPermissionChanges({});
                                  }}
                                >
                                  Cancel
                                </Button>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center space-x-2">
                            <Switch
                              id={`role-active-${role}`}
                              checked={roleData.is_active !== false}
                              onCheckedChange={() => handleToggleRoleActive(role, roleData.is_active !== false)}
                              disabled={role === 'admin' || role === 'manager'}
                            />
                            <Label htmlFor={`role-active-${role}`} className="text-xs text-gray-600 cursor-pointer">
                              {roleData.is_active !== false ? 'Role enabled' : 'Role disabled'}
                            </Label>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-3">
                          {availablePermissions.map((group) => (
                            <div key={group.group} className="space-y-2">
                              <h4 className="text-sm font-medium text-gray-700 border-b pb-1">
                                {group.group}
                              </h4>
                              <div className="space-y-1">
                                {group.permissions.map((perm) => {
                                  const hasPermission = currentPermissions.includes(perm.permission);
                                  return (
                                    <div key={perm.permission} className="flex items-start space-x-2">
                                      <input
                                        type="checkbox"
                                        id={`${role}-${perm.permission}`}
                                        checked={hasPermission}
                                        disabled={!isEditing || role === 'admin'}
                                        onChange={(e) => {
                                          if (isEditing && role !== 'admin') {
                                            const updated = e.target.checked
                                              ? [...(permissionChanges[role] || rolePerms.permissions), perm.permission]
                                              : (permissionChanges[role] || rolePerms.permissions).filter(p => p !== perm.permission);
                                            
                                            setPermissionChanges({
                                              ...permissionChanges,
                                              [role]: updated
                                            });
                                          }
                                        }}
                                        className="rounded mt-1"
                                      />
                                      <div>
                                        <label 
                                          htmlFor={`${role}-${perm.permission}`}
                                          className={`text-sm cursor-pointer ${
                                            hasPermission ? 'text-purple-700 font-medium' : 'text-gray-600'
                                          } ${!isEditing || role === 'admin' ? 'cursor-not-allowed' : ''}`}
                                        >
                                          {perm.permission.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </label>
                                        <p className="text-xs text-gray-500">{perm.description}</p>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                          {role === 'admin' && (
                            <div className="mt-4 p-2 bg-yellow-50 border border-yellow-200 rounded">
                              <p className="text-xs text-yellow-700">
                                <Shield className="h-3 w-3 inline mr-1" />
                                Admin role has all permissions by default and cannot be modified.
                              </p>
                            </div>
                          )}
                          <div className="mt-3 text-xs text-gray-500">
                            Permissions: {currentPermissions.length} of {availablePermissions.reduce((acc, group) => acc + group.permissions.length, 0)}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Shift Templates Tab */}
      {activeTab === 'templates' && (
        <Card className="shadow-lg border-0">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-blue-600" />
                <span>Shift Template Management</span>
              </CardTitle>
              <Button 
                onClick={() => {
                  resetTemplateForm();
                  setShowTemplateModal(true);
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Template
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {shiftTemplates.map((template) => (
                  <div key={template.id} className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200 hover:shadow-md transition-shadow">
                    <div className="flex items-start justify-between mb-3">
                      <h3 className="font-semibold text-gray-900 text-lg">{template.name}</h3>
                      <div className="flex items-center space-x-1">
                        {template.work_location === 'remote' && (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs font-medium rounded">
                            🏠 Remote
                          </span>
                        )}
                        {template.work_location === 'office' && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                            🏢 Office
                          </span>
                        )}
                        {template.work_location === 'hybrid' && (
                          <span className="px-2 py-1 bg-purple-100 text-purple-700 text-xs font-medium rounded">
                            🔄 Hybrid
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-gray-700 space-y-2 mb-4">
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-gray-500" />
                        <span><strong>Time:</strong> {template.start_time} - {template.end_time}</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-gray-500" />
                        <span><strong>Duration:</strong> {template.duration_hours} hours</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Settings className="h-4 w-4 text-gray-500" />
                        <span><strong>Type:</strong> {template.shift_type}</span>
                      </div>
                    </div>
                    <div className="flex justify-end space-x-2 mt-3 pt-3 border-t border-blue-200">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleEditTemplate(template)}
                        className="hover:bg-blue-600 hover:text-white"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button 
                        size="sm" 
                        variant="destructive" 
                        onClick={() => handleDeleteTemplate(template.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {shiftTemplates.length === 0 && (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    <Clock className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No shift templates found</p>
                    <p className="text-sm">Templates will appear here when created</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Shift Management Tab removed - shift deletion available directly in ScheduleManager */}

      {/* Support Channels Tab */}
      {activeTab === 'channels' && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <Card className="shadow-lg border-0">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Plus className="h-5 w-5 text-green-600" />
                <span>Add Support Channel</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="channel-name">Channel Name</Label>
                  <Input
                    id="channel-name"
                    type="text"
                    placeholder="e.g., live-chat, social-media"
                    value={channelForm.name}
                    onChange={(e) => setChannelForm({...channelForm, name: e.target.value})}
                  />
                </div>
                <Button onClick={addChannel} disabled={!channelForm.name.trim()}>
                  Add Channel
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-lg border-0">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                <span>Current Channels ({channels.length})</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {channels.map((channel) => (
                  <div key={channel} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center space-x-3">
                      {getChannelIcon(channel)}
                      <span className="font-medium capitalize text-gray-900">{channel}</span>
                    </div>
                    <Button size="sm" variant="destructive" onClick={() => removeChannel(channel)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* PTO Types Tab */}
      {activeTab === 'ptotypes' && (
        <div className="space-y-6">
          {/* Header with Add Button */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <Calendar className="h-5 w-5 text-purple-600" />
                    <span>PTO Type Management</span>
                  </CardTitle>
                  <CardDescription>Define and manage PTO types for your organization</CardDescription>
                </div>
                <Button
                  onClick={() => openPtoTypeModal()}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add PTO Type
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {ptoTypeConfigs.map((ptoType) => {
                  const IconComponent = getPtoTypeIcon(ptoType.icon);
                  const colorMap = {
                    blue: { accent: 'bg-blue-600', border: 'border-blue-200', bg: 'from-blue-50' },
                    purple: { accent: 'bg-purple-600', border: 'border-purple-200', bg: 'from-purple-50' },
                    red: { accent: 'bg-red-600', border: 'border-red-200', bg: 'from-red-50' },
                    green: { accent: 'bg-green-600', border: 'border-green-200', bg: 'from-green-50' },
                    yellow: { accent: 'bg-yellow-600', border: 'border-yellow-200', bg: 'from-yellow-50' },
                    orange: { accent: 'bg-orange-600', border: 'border-orange-200', bg: 'from-orange-50' },
                    gray: { accent: 'bg-gray-600', border: 'border-gray-200', bg: 'from-gray-50' }
                  };
                  const colors = colorMap[ptoType.color] || colorMap.blue;
                  
                  return (
                    <Card key={ptoType.id} className={`${!ptoType.is_active ? 'opacity-50' : ''}`}>
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className={`${colors.accent} text-white p-2 rounded-lg`}>
                              <IconComponent className="h-5 w-5" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">{ptoType.display_name}</h3>
                              <p className="text-xs text-gray-500">{ptoType.value}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {ptoType.is_system && (
                              <Badge variant="secondary" className="text-xs">System</Badge>
                            )}
                            <Switch
                              checked={ptoType.is_active}
                              onCheckedChange={() => handleTogglePtoTypeActive(ptoType)}
                            />
                          </div>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-3">{ptoType.description}</p>
                        
                        <div className="text-xs text-gray-600 space-y-1 mb-3 pb-3 border-b">
                          <div className="flex items-center justify-between">
                            <span>Requires Approval:</span>
                            <span className="font-medium">{ptoType.requires_approval ? 'Yes' : 'No'}</span>
                          </div>
                          <div className="flex items-center justify-between">
                            <span>Requires Documentation:</span>
                            <span className="font-medium">{ptoType.requires_documentation ? 'Yes' : 'No'}</span>
                          </div>
                          {ptoType.max_days_per_year && (
                            <div className="flex items-center justify-between">
                              <span>Max Days/Year:</span>
                              <span className="font-medium">{ptoType.max_days_per_year}</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2 text-xs text-gray-500">
                            <span className={`inline-block w-3 h-3 rounded-full ${colors.accent}`}></span>
                            <span>{ptoType.color}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              onClick={() => openPtoTypeModal(ptoType)}
                              variant="outline"
                              size="sm"
                            >
                              <Edit className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                            <Button
                              onClick={() => handleDeletePtoType(ptoType)}
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              
              {ptoTypeConfigs.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>No PTO types configured yet.</p>
                  <p className="text-sm">Click "Add PTO Type" to create your first one.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Information Card */}
          <Card className="bg-purple-50 border-purple-200">
            <CardContent className="pt-6">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 text-purple-600" />
                About PTO Types
              </h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start space-x-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span><strong>System Types:</strong> Vacation, Sick Leave, and Personal Time are protected and cannot be deleted (but can be disabled)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span><strong>Custom Types:</strong> Create PTO types specific to your organization (Bereavement, Parental Leave, Jury Duty, etc.)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span><strong>Approval Settings:</strong> Configure whether each PTO type requires manager approval</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-orange-600 font-bold">•</span>
                  <span><strong>Max Days:</strong> Set annual limits for specific PTO types (e.g., 10 sick days per year)</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      )}

      {/* PTO Type Modal */}
      <Dialog open={showPtoTypeModal} onOpenChange={setShowPtoTypeModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingPtoType ? 'Edit PTO Type' : 'Add New PTO Type'}</DialogTitle>
            <DialogDescription>
              {editingPtoType ? 'Update the PTO type details' : 'Create a new PTO type for your organization'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {!editingPtoType && (
              <div>
                <Label htmlFor="pto-type-value">Value (Unique Identifier) *</Label>
                <Input
                  id="pto-type-value"
                  value={ptoTypeForm.value}
                  onChange={(e) => setPtoTypeForm({...ptoTypeForm, value: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                  placeholder="e.g., bereavement, parental, jury_duty"
                />
                <p className="text-xs text-gray-500 mt-1">Lowercase letters, numbers, and underscores only</p>
              </div>
            )}
            
            <div>
              <Label htmlFor="pto-type-name">Display Name *</Label>
              <Input
                id="pto-type-name"
                value={ptoTypeForm.display_name}
                onChange={(e) => setPtoTypeForm({...ptoTypeForm, display_name: e.target.value})}
                placeholder="e.g., Bereavement Leave, Parental Leave"
              />
            </div>
            
            <div>
              <Label htmlFor="pto-type-desc">Description *</Label>
              <Input
                id="pto-type-desc"
                value={ptoTypeForm.description}
                onChange={(e) => setPtoTypeForm({...ptoTypeForm, description: e.target.value})}
                placeholder="e.g., Time off for family bereavement"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="pto-type-icon">Icon</Label>
                <Select
                  value={ptoTypeForm.icon}
                  onValueChange={(value) => setPtoTypeForm({...ptoTypeForm, icon: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Calendar">Calendar</SelectItem>
                    <SelectItem value="Plane">Plane (Vacation)</SelectItem>
                    <SelectItem value="Heart">Heart (Sick)</SelectItem>
                    <SelectItem value="User">User (Personal)</SelectItem>
                    <SelectItem value="Users">Users (Family)</SelectItem>
                    <SelectItem value="Home">Home</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="pto-type-color">Color</Label>
                <Select
                  value={ptoTypeForm.color}
                  onValueChange={(value) => setPtoTypeForm({...ptoTypeForm, color: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="blue">Blue</SelectItem>
                    <SelectItem value="purple">Purple</SelectItem>
                    <SelectItem value="red">Red</SelectItem>
                    <SelectItem value="green">Green</SelectItem>
                    <SelectItem value="yellow">Yellow</SelectItem>
                    <SelectItem value="orange">Orange</SelectItem>
                    <SelectItem value="gray">Gray</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label htmlFor="pto-max-days">Max Days Per Year (Optional)</Label>
              <Input
                id="pto-max-days"
                type="number"
                min="0"
                value={ptoTypeForm.max_days_per_year || ''}
                onChange={(e) => setPtoTypeForm({...ptoTypeForm, max_days_per_year: e.target.value ? parseInt(e.target.value) : null})}
                placeholder="Leave empty for unlimited"
              />
            </div>
            
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="requires-approval"
                  checked={ptoTypeForm.requires_approval}
                  onCheckedChange={(checked) => setPtoTypeForm({...ptoTypeForm, requires_approval: checked})}
                />
                <Label htmlFor="requires-approval" className="cursor-pointer">
                  Requires manager approval
                </Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="requires-documentation"
                  checked={ptoTypeForm.requires_documentation}
                  onCheckedChange={(checked) => setPtoTypeForm({...ptoTypeForm, requires_documentation: checked})}
                />
                <Label htmlFor="requires-documentation" className="cursor-pointer">
                  Requires supporting documentation
                </Label>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowPtoTypeModal(false)} variant="outline">
              Cancel
            </Button>
            <Button
              onClick={editingPtoType ? handleUpdatePtoType : handleCreatePtoType}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              disabled={!ptoTypeForm.display_name || !ptoTypeForm.description || (!editingPtoType && !ptoTypeForm.value)}
            >
              {editingPtoType ? 'Update' : 'Create'} PTO Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activity Types Tab */}
      {activeTab === 'activitytypes' && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Activity Type Form */}
          <Card className="shadow-lg border-0 xl:col-span-1">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Activity className="h-5 w-5 text-blue-600" />
                <span>{editingActivityType ? 'Edit Activity Type' : 'Add Activity Type'}</span>
              </CardTitle>
              <CardDescription>
                Configure activity types for scheduling client visits, calls, meetings, etc.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="activity-type-name">Name *</Label>
                  <Input
                    id="activity-type-name"
                    placeholder="e.g., Client Visit"
                    value={activityTypeForm.name}
                    onChange={(e) => setActivityTypeForm({...activityTypeForm, name: e.target.value})}
                  />
                </div>

                <div>
                  <Label htmlFor="activity-type-value">Value (identifier) *</Label>
                  <Input
                    id="activity-type-value"
                    placeholder="e.g., client_visit (no spaces)"
                    value={activityTypeForm.value}
                    onChange={(e) => setActivityTypeForm({...activityTypeForm, value: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                  />
                  <p className="text-xs text-gray-500 mt-1">Lowercase, use underscores instead of spaces</p>
                </div>

                <div>
                  <Label htmlFor="activity-type-icon">Icon (Emoji)</Label>
                  <Input
                    id="activity-type-icon"
                    placeholder="Click an emoji below or type your own"
                    value={activityTypeForm.icon}
                    onChange={(e) => setActivityTypeForm({...activityTypeForm, icon: e.target.value})}
                    className="mb-2"
                  />
                  <div className="grid grid-cols-8 gap-1 p-2 bg-gray-50 rounded border border-gray-200 max-h-32 overflow-y-auto">
                    {/* Meeting & Collaboration */}
                    {['👥', '🤝', '💼', '📊', '🎯', '🗣️', '👔'].map(emoji => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => setActivityTypeForm({...activityTypeForm, icon: emoji})}
                        className="text-2xl hover:bg-gray-200 p-1 rounded transition-colors"
                        title="Select emoji"
                      >
                        {emoji}
                      </button>
                    ))}
                    {/* Communication */}
                    {['📞', '📱', '💬', '📧', '🎤'].map(emoji => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => setActivityTypeForm({...activityTypeForm, icon: emoji})}
                        className="text-2xl hover:bg-gray-200 p-1 rounded transition-colors"
                        title="Select emoji"
                      >
                        {emoji}
                      </button>
                    ))}
                    {/* Client/Customer */}
                    {['✈️', '🚗', '🏢', '👨‍💼'].map(emoji => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => setActivityTypeForm({...activityTypeForm, icon: emoji})}
                        className="text-2xl hover:bg-gray-200 p-1 rounded transition-colors"
                        title="Select emoji"
                      >
                        {emoji}
                      </button>
                    ))}
                    {/* Training */}
                    {['📚', '🎓', '🧑‍🏫', '📖', '💡'].map(emoji => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => setActivityTypeForm({...activityTypeForm, icon: emoji})}
                        className="text-2xl hover:bg-gray-200 p-1 rounded transition-colors"
                        title="Select emoji"
                      >
                        {emoji}
                      </button>
                    ))}
                    {/* Events */}
                    {['🎉', '🏛️', '🎊', '🍕', '☕'].map(emoji => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => setActivityTypeForm({...activityTypeForm, icon: emoji})}
                        className="text-2xl hover:bg-gray-200 p-1 rounded transition-colors"
                        title="Select emoji"
                      >
                        {emoji}
                      </button>
                    ))}
                    {/* Technical */}
                    {['💻', '🔧', '🐛', '🚀', '🛠️'].map(emoji => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => setActivityTypeForm({...activityTypeForm, icon: emoji})}
                        className="text-2xl hover:bg-gray-200 p-1 rounded transition-colors"
                        title="Select emoji"
                      >
                        {emoji}
                      </button>
                    ))}
                    {/* Other */}
                    {['⏰', '📝', '🔍', '✅', '🎨', '🏥', '🌟', '📢'].map(emoji => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => setActivityTypeForm({...activityTypeForm, icon: emoji})}
                        className="text-2xl hover:bg-gray-200 p-1 rounded transition-colors"
                        title="Select emoji"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label htmlFor="activity-type-color">Color (Hex)</Label>
                  <div className="flex space-x-2">
                    <Input
                      id="activity-type-color"
                      type="color"
                      value={activityTypeForm.color}
                      onChange={(e) => setActivityTypeForm({...activityTypeForm, color: e.target.value})}
                      className="w-20"
                    />
                    <Input
                      value={activityTypeForm.color}
                      onChange={(e) => setActivityTypeForm({...activityTypeForm, color: e.target.value})}
                      placeholder="#3B82F6"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="activity-type-description">Description (Optional)</Label>
                  <Input
                    id="activity-type-description"
                    placeholder="Brief description of this activity type"
                    value={activityTypeForm.description}
                    onChange={(e) => setActivityTypeForm({...activityTypeForm, description: e.target.value})}
                  />
                </div>

                <div className="flex space-x-2">
                  {editingActivityType && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setEditingActivityType(null);
                        setActivityTypeForm({
                          name: '',
                          value: '',
                          icon: '📅',
                          color: '#3B82F6',
                          description: ''
                        });
                      }}
                    >
                      Cancel
                    </Button>
                  )}
                  <Button onClick={handleSaveActivityType} className="flex-1">
                    {editingActivityType ? 'Update' : 'Create'} Activity Type
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Activity Types List */}
          <Card className="shadow-lg border-0 xl:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Activity className="h-5 w-5 text-blue-600" />
                <span>Activity Types</span>
              </CardTitle>
              <CardDescription>
                Manage available activity types for your organization
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {activityTypes.map(type => (
                  <div
                    key={type.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex items-center space-x-4">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
                        style={{ backgroundColor: type.color + '20', border: `2px solid ${type.color}` }}
                      >
                        {type.icon}
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900">{type.name}</div>
                        <div className="text-sm text-gray-600">{type.value}</div>
                        {type.description && (
                          <div className="text-xs text-gray-500 mt-1">{type.description}</div>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingActivityType(type);
                          setActivityTypeForm({
                            name: type.name,
                            value: type.value,
                            icon: type.icon,
                            color: type.color,
                            description: type.description || ''
                          });
                        }}
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteActivityType(type.id)}
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
                {activityTypes.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    <Activity className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                    <p>No activity types found</p>
                    <p className="text-sm">Create your first activity type to get started</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Lunch Settings Tab */}
      {/* Lunch & Breaks Management Tab */}
      {activeTab === 'lunch' && (
        <div className="space-y-6">
          <Card className="shadow-lg border-0">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <Clock className="h-5 w-5 text-orange-600" />
                    <span>Lunch & Breaks Configuration</span>
                  </CardTitle>
                  <CardDescription className="mt-2">
                    Configure lunch periods and break policies for your organization
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {!lunchBreakPolicy ? (
                <div className="text-center py-8">
                  <Clock className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p className="text-gray-500">Loading configuration...</p>
                </div>
              ) : (
                <div className="space-y-8">
                  {/* Master Toggle */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-semibold text-blue-900">Enable Lunch & Break Management</h3>
                        <p className="text-sm text-blue-700 mt-1">
                          Turn this feature on to enforce lunch and break policies across all shifts
                        </p>
                      </div>
                      <Switch
                        checked={lunchBreakPolicy.feature_enabled}
                        onCheckedChange={(checked) => {
                          const updated = { ...lunchBreakPolicy, feature_enabled: checked };
                          setLunchBreakPolicy(updated);
                          updateLunchBreakPolicy(updated);
                        }}
                      />
                    </div>
                  </div>

                  {lunchBreakPolicy.feature_enabled && (
                    <>
                      {/* Lunch Configuration Section */}
                      <div className="border rounded-lg p-6 space-y-6">
                        <div className="flex items-center justify-between">
                          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <Activity className="h-5 w-5 mr-2 text-orange-600" />
                            Lunch Period Settings
                          </h3>
                          <Switch
                            checked={lunchBreakPolicy.lunch_enabled}
                            onCheckedChange={(checked) => {
                              const updated = { ...lunchBreakPolicy, lunch_enabled: checked };
                              setLunchBreakPolicy(updated);
                            }}
                          />
                        </div>

                        {lunchBreakPolicy.lunch_enabled && (
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label>Lunch Duration (minutes)</Label>
                              <Input
                                type="number"
                                min="15"
                                max="120"
                                value={lunchBreakPolicy.lunch_duration}
                                onChange={(e) => setLunchBreakPolicy({
                                  ...lunchBreakPolicy,
                                  lunch_duration: parseInt(e.target.value) || 30
                                })}
                              />
                            </div>

                            <div className="space-y-2">
                              <Label className="flex items-center space-x-2">
                                <span>Lunch is Paid Time</span>
                                <Switch
                                  checked={lunchBreakPolicy.lunch_paid}
                                  onCheckedChange={(checked) => setLunchBreakPolicy({
                                    ...lunchBreakPolicy,
                                    lunch_paid: checked
                                  })}
                                />
                              </Label>
                              <p className="text-xs text-gray-500">
                                {lunchBreakPolicy.lunch_paid ? 'Employees are paid during lunch' : 'Lunch is unpaid time'}
                              </p>
                            </div>

                            <div className="space-y-2">
                              <div className="flex items-center justify-between">
                                <div>
                                  <Label>Restrict Lunch Window</Label>
                                  <p className="text-xs text-gray-500">
                                    {lunchBreakPolicy.lunch_window_restricted !== false
                                      ? 'Lunch must be taken within the time window below'
                                      : 'Lunch can be taken anytime during the shift (ideal for 24-hour operations)'}
                                  </p>
                                </div>
                                <Switch
                                  checked={lunchBreakPolicy.lunch_window_restricted !== false}
                                  onCheckedChange={(checked) => setLunchBreakPolicy({
                                    ...lunchBreakPolicy,
                                    lunch_window_restricted: checked
                                  })}
                                />
                              </div>
                            </div>

                            {lunchBreakPolicy.lunch_window_restricted !== false && (
                            <>
                            <div className="space-y-2">
                              <Label>Lunch Window Start</Label>
                              <Input
                                type="time"
                                value={lunchBreakPolicy.lunch_start_window}
                                onChange={(e) => setLunchBreakPolicy({
                                  ...lunchBreakPolicy,
                                  lunch_start_window: e.target.value
                                })}
                              />
                            </div>

                            <div className="space-y-2">
                              <Label>Lunch Window End</Label>
                              <Input
                                type="time"
                                value={lunchBreakPolicy.lunch_end_window}
                                onChange={(e) => setLunchBreakPolicy({
                                  ...lunchBreakPolicy,
                                  lunch_end_window: e.target.value
                                })}
                              />
                            </div>
                            </>
                            )}

                            <div className="space-y-2">
                              <Label>Min Hours Before Lunch</Label>
                              <Input
                                type="number"
                                step="0.5"
                                min="0"
                                max="8"
                                value={lunchBreakPolicy.lunch_min_hours_before}
                                onChange={(e) => setLunchBreakPolicy({
                                  ...lunchBreakPolicy,
                                  lunch_min_hours_before: parseFloat(e.target.value) || 0
                                })}
                              />
                              <p className="text-xs text-gray-500">Must work this many hours before lunch</p>
                            </div>

                            <div className="space-y-2">
                              <Label>Min Hours Until Shift End</Label>
                              <Input
                                type="number"
                                step="0.5"
                                min="0"
                                max="8"
                                value={lunchBreakPolicy.lunch_min_hours_until_end}
                                onChange={(e) => setLunchBreakPolicy({
                                  ...lunchBreakPolicy,
                                  lunch_min_hours_until_end: parseFloat(e.target.value) || 0
                                })}
                              />
                              <p className="text-xs text-gray-500">Can't take lunch this close to shift end</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Break Types Section */}
                      <div className="border rounded-lg p-6 space-y-6">
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                              <Clock className="h-5 w-5 mr-2 text-green-600" />
                              Break Types
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">Manage different types of breaks employees can take</p>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={lunchBreakPolicy.breaks_enabled}
                              onCheckedChange={(checked) => {
                                const updated = { ...lunchBreakPolicy, breaks_enabled: checked };
                                setLunchBreakPolicy(updated);
                              }}
                            />
                            {lunchBreakPolicy.breaks_enabled && (
                              <Button size="sm" onClick={() => setShowBreakTypeModal(true)}>
                                <Plus className="h-4 w-4 mr-1" />
                                Add Break Type
                              </Button>
                            )}
                          </div>
                        </div>

                        {lunchBreakPolicy.breaks_enabled && (
                          <div>
                            {lunchBreakPolicy.break_types?.length === 0 ? (
                              <div className="text-center py-8 text-gray-500">
                                <Clock className="h-10 w-10 mx-auto mb-2 opacity-50" />
                                <p>No break types configured</p>
                                <p className="text-sm">Click "Add Break Type" to create one</p>
                              </div>
                            ) : (
                              <div className="grid gap-3">
                                {lunchBreakPolicy.break_types?.map((breakType) => (
                                  <div
                                    key={breakType.id}
                                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border"
                                  >
                                    <div className="flex items-center space-x-3">
                                      <Clock className="h-5 w-5 text-green-600" />
                                      <div>
                                        <p className="font-medium">{breakType.name}</p>
                                        <p className="text-sm text-gray-500">
                                          {breakType.duration} minutes • {breakType.paid ? 'Paid' : 'Unpaid'}
                                        </p>
                                      </div>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleDeleteBreakType(breakType.id)}
                                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Break Frequency Rules */}
                      {lunchBreakPolicy.breaks_enabled && lunchBreakPolicy.break_types?.length > 0 && (
                        <div className="border rounded-lg p-6 space-y-4">
                          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                            <Activity className="h-5 w-5 mr-2 text-purple-600" />
                            Break Frequency Rules
                          </h3>
                          <p className="text-sm text-gray-500">Define how many breaks employees get based on shift length</p>

                          <div className="grid gap-4">
                            {Object.entries(lunchBreakPolicy.break_rules || {}).map(([range, count]) => (
                              <div key={range} className="flex items-center space-x-4">
                                <Label className="w-32">{range} hours:</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  max="5"
                                  value={count}
                                  onChange={(e) => {
                                    const newRules = { ...lunchBreakPolicy.break_rules };
                                    newRules[range] = parseInt(e.target.value) || 0;
                                    setLunchBreakPolicy({
                                      ...lunchBreakPolicy,
                                      break_rules: newRules
                                    });
                                  }}
                                  className="w-24"
                                />
                                <span className="text-sm text-gray-500">break(s)</span>
                              </div>
                            ))}
                          </div>

                          <div className="space-y-2 pt-4 border-t">
                            <Label>Minimum Gap Between Breaks (minutes)</Label>
                            <Input
                              type="number"
                              min="15"
                              max="240"
                              value={lunchBreakPolicy.break_min_gap_minutes}
                              onChange={(e) => setLunchBreakPolicy({
                                ...lunchBreakPolicy,
                                break_min_gap_minutes: parseInt(e.target.value) || 60
                              })}
                              className="w-48"
                            />
                          </div>

                          <div className="flex items-center space-x-2 pt-2">
                            <Switch
                              checked={lunchBreakPolicy.break_evenly_spaced}
                              onCheckedChange={(checked) => setLunchBreakPolicy({
                                ...lunchBreakPolicy,
                                break_evenly_spaced: checked
                              })}
                            />
                            <Label>Auto-calculate evenly spaced breaks</Label>
                          </div>
                        </div>
                      )}

                      {/* Validation Rules */}
                      <div className="border rounded-lg p-6 space-y-4">
                        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                          <Shield className="h-5 w-5 mr-2 text-blue-600" />
                          Validation Rules
                        </h3>

                        <div className="space-y-3">
                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={lunchBreakPolicy.prevent_overlap}
                              onCheckedChange={(checked) => setLunchBreakPolicy({
                                ...lunchBreakPolicy,
                                prevent_overlap: checked
                              })}
                            />
                            <Label>Prevent overlapping lunch/breaks</Label>
                          </div>

                          <div className="flex items-center space-x-2">
                            <Switch
                              checked={lunchBreakPolicy.breaks_within_work_hours}
                              onCheckedChange={(checked) => setLunchBreakPolicy({
                                ...lunchBreakPolicy,
                                breaks_within_work_hours: checked
                              })}
                            />
                            <Label>Breaks must be within working hours</Label>
                          </div>
                        </div>
                      </div>

                      {/* Save Button */}
                      <div className="flex justify-end space-x-2 pt-4 border-t">
                        <Button
                          variant="outline"
                          onClick={() => fetchLunchBreakPolicy()}
                        >
                          Reset Changes
                        </Button>
                        <Button
                          onClick={() => updateLunchBreakPolicy(lunchBreakPolicy)}
                          disabled={loading}
                        >
                          {loading ? 'Saving...' : 'Save Configuration'}
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Break Type Modal */}
      <Dialog open={showBreakTypeModal} onOpenChange={setShowBreakTypeModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Break Type</DialogTitle>
            <DialogDescription>
              Create a new break type for your organization
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Break Name *</Label>
              <Input
                placeholder="e.g., 15-minute break"
                value={breakTypeForm.name}
                onChange={(e) => setBreakTypeForm({ ...breakTypeForm, name: e.target.value })}
              />
            </div>

            <div className="space-y-2">
              <Label>Duration (minutes) *</Label>
              <Input
                type="number"
                min="5"
                max="60"
                value={breakTypeForm.duration}
                onChange={(e) => setBreakTypeForm({ 
                  ...breakTypeForm, 
                  duration: parseInt(e.target.value) || 15 
                })}
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                checked={breakTypeForm.paid}
                onCheckedChange={(checked) => setBreakTypeForm({ ...breakTypeForm, paid: checked })}
              />
              <Label>This is paid time</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBreakTypeModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddBreakType} disabled={loading || !breakTypeForm.name}>
              {loading ? 'Adding...' : 'Add Break Type'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Template Creation/Edit Modal */}
      <Dialog open={showTemplateModal} onOpenChange={(open) => {
        setShowTemplateModal(open);
        if (!open) resetTemplateForm();
      }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {templateForm.isEditing ? 'Edit Shift Template' : 'Create Shift Template'}
            </DialogTitle>
            <DialogDescription>
              {templateForm.isEditing 
                ? 'Update the shift template details' 
                : 'Define a reusable shift template for scheduling'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label htmlFor="template-name">Template Name *</Label>
              <Input
                id="template-name"
                placeholder="e.g., Afternoon Shift, Evening Coverage"
                value={templateForm.name}
                onChange={(e) => setTemplateForm({...templateForm, name: e.target.value})}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="start-time">Start Time *</Label>
                <Input
                  id="start-time"
                  type="time"
                  value={templateForm.start_time}
                  onChange={(e) => setTemplateForm({...templateForm, start_time: e.target.value})}
                />
              </div>
              <div>
                <Label htmlFor="end-time">End Time *</Label>
                <Input
                  id="end-time"
                  type="time"
                  value={templateForm.end_time}
                  onChange={(e) => setTemplateForm({...templateForm, end_time: e.target.value})}
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="shift-type">Shift Type</Label>
                <Select
                  value={templateForm.shift_type}
                  onValueChange={(value) => setTemplateForm({...templateForm, shift_type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="regular">Regular</SelectItem>
                    <SelectItem value="on_call">On-Call</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="work-location">Work Location *</Label>
                <Select
                  value={templateForm.work_location}
                  onValueChange={(value) => setTemplateForm({...templateForm, work_location: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="office">🏢 Office</SelectItem>
                    <SelectItem value="remote">🏠 Remote</SelectItem>
                    <SelectItem value="hybrid">🔄 Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> Work location indicates whether this shift is typically performed in the office, remotely, or as a hybrid arrangement.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowTemplateModal(false);
              resetTemplateForm();
            }}>
              Cancel
            </Button>
            <Button onClick={handleCreateTemplate} className="bg-blue-600 hover:bg-blue-700">
              {templateForm.isEditing ? 'Update Template' : 'Create Template'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Shift Types Tab */}
      {activeTab === 'shifttypes' && (
        <div className="space-y-6">
          {/* Header with Add Button */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <Settings className="h-5 w-5 text-blue-600" />
                    <span>Shift Type Management</span>
                  </CardTitle>
                  <CardDescription>Define and manage shift types for your organization</CardDescription>
                </div>
                <Button
                  onClick={() => openShiftTypeModal()}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Shift Type
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {shiftTypeConfigs.map((shiftType) => {
                  const IconComponent = getShiftTypeIcon(shiftType.value);
                  const colors = getShiftTypeColor(shiftType.value);
                  
                  return (
                    <Card key={shiftType.id} className={`${!shiftType.is_active ? 'opacity-50' : ''}`}>
                      <CardContent className="pt-6">
                        <div className="flex items-start justify-between mb-4">
                          <div className="flex items-center space-x-3">
                            <div className={`${colors.accent} text-white p-2 rounded-lg`}>
                              <IconComponent className="h-5 w-5" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-gray-900">{shiftType.display_name}</h3>
                              <p className="text-xs text-gray-500">{shiftType.value}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            {shiftType.is_system && (
                              <Badge variant="secondary" className="text-xs">System</Badge>
                            )}
                            <Switch
                              checked={shiftType.is_active}
                              onCheckedChange={() => handleToggleShiftTypeActive(shiftType)}
                            />
                          </div>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-4">{shiftType.description}</p>
                        
                        <div className="flex items-center justify-between pt-4 border-t">
                          <div className="flex items-center space-x-2 text-xs text-gray-500">
                            <span className={`inline-block w-3 h-3 rounded-full ${colors.accent}`}></span>
                            <span>{shiftType.color}</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Button
                              onClick={() => openShiftTypeModal(shiftType)}
                              variant="outline"
                              size="sm"
                            >
                              <Edit className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                            {!shiftType.is_system && (
                              <Button
                                onClick={() => handleDeleteShiftType(shiftType)}
                                variant="outline"
                                size="sm"
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              
              {shiftTypeConfigs.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  <Settings className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p>No shift types configured yet.</p>
                  <p className="text-sm">Click "Add Shift Type" to create your first one.</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Information Card */}
          <Card className="bg-blue-50 border-blue-200">
            <CardContent className="pt-6">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 text-blue-600" />
                About Shift Types
              </h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start space-x-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span><strong>System Types:</strong> Regular, On-Call, and PTO are protected and cannot be deleted (but can be disabled)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span><strong>Custom Types:</strong> Create shift types specific to your organization (Training, Emergency, Maintenance, etc.)</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span><strong>Auto-Configuration:</strong> New shift types automatically get default lunch settings</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-orange-600 font-bold">•</span>
                  <span><strong>Disable vs Delete:</strong> Disable shift types in use; delete only unused custom types</span>
                </li>
              </ul>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Shift Type Modal */}
      <Dialog open={showShiftTypeModal} onOpenChange={setShowShiftTypeModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingShiftType ? 'Edit Shift Type' : 'Add New Shift Type'}</DialogTitle>
            <DialogDescription>
              {editingShiftType ? 'Update the shift type details' : 'Create a new shift type for your organization'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {!editingShiftType && (
              <div>
                <Label htmlFor="shift-type-value">Value (Unique Identifier) *</Label>
                <Input
                  id="shift-type-value"
                  value={shiftTypeForm.value}
                  onChange={(e) => setShiftTypeForm({...shiftTypeForm, value: e.target.value.toLowerCase().replace(/\s+/g, '_')})}
                  placeholder="e.g., training, emergency, maintenance"
                />
                <p className="text-xs text-gray-500 mt-1">Lowercase letters, numbers, and underscores only</p>
              </div>
            )}
            
            <div>
              <Label htmlFor="shift-type-name">Display Name *</Label>
              <Input
                id="shift-type-name"
                value={shiftTypeForm.display_name}
                onChange={(e) => setShiftTypeForm({...shiftTypeForm, display_name: e.target.value})}
                placeholder="e.g., Training, Emergency Response"
              />
            </div>
            
            <div>
              <Label htmlFor="shift-type-desc">Description *</Label>
              <Input
                id="shift-type-desc"
                value={shiftTypeForm.description}
                onChange={(e) => setShiftTypeForm({...shiftTypeForm, description: e.target.value})}
                placeholder="e.g., Employee training and development sessions"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="shift-type-icon">Icon</Label>
                <Select
                  value={shiftTypeForm.icon}
                  onValueChange={(value) => setShiftTypeForm({...shiftTypeForm, icon: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Clock">Clock</SelectItem>
                    <SelectItem value="Phone">Phone</SelectItem>
                    <SelectItem value="Calendar">Calendar</SelectItem>
                    <SelectItem value="AlertCircle">Alert Circle</SelectItem>
                    <SelectItem value="Users">Users</SelectItem>
                    <SelectItem value="Settings">Settings</SelectItem>
                    <SelectItem value="Shield">Shield</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="shift-type-color">Color</Label>
                <Select
                  value={shiftTypeForm.color}
                  onValueChange={(value) => setShiftTypeForm({...shiftTypeForm, color: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="blue">Blue</SelectItem>
                    <SelectItem value="orange">Orange</SelectItem>
                    <SelectItem value="purple">Purple</SelectItem>
                    <SelectItem value="red">Red</SelectItem>
                    <SelectItem value="green">Green</SelectItem>
                    <SelectItem value="gray">Gray</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Preview */}
            <div className="pt-4 border-t">
              <Label className="mb-2">Preview</Label>
              <div className={`bg-gradient-to-r ${getShiftTypeColor(shiftTypeForm.color).bg} border ${getShiftTypeColor(shiftTypeForm.color).border} rounded-lg p-4`}>
                <div className="flex items-center space-x-3">
                  <div className={`${getShiftTypeColor(shiftTypeForm.color).accent} text-white p-2 rounded-lg`}>
                    {React.createElement(getShiftTypeIcon(shiftTypeForm.icon), { className: "h-5 w-5" })}
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{shiftTypeForm.display_name || 'Display Name'}</h3>
                    <p className="text-sm text-gray-600">{shiftTypeForm.description || 'Description'}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button onClick={() => setShowShiftTypeModal(false)} variant="outline">
              Cancel
            </Button>
            <Button
              onClick={editingShiftType ? handleUpdateShiftType : handleCreateShiftType}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              disabled={!shiftTypeForm.display_name || !shiftTypeForm.description || (!editingShiftType && !shiftTypeForm.value)}
            >
              {editingShiftType ? 'Update' : 'Create'} Shift Type
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Flex Bank Settings Tab */}
      {activeTab === 'flexbank' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Clock className="h-5 w-5 text-blue-600" />
                <span>Lunch Period Settings</span>
              </CardTitle>
              <CardDescription>Configure lunch break settings for each shift type dynamically</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              
              {/* Dynamically render shift type cards */}
              {shiftTypes.length > 0 ? (
                shiftTypes.map((shiftType) => {
                  const IconComponent = getShiftTypeIcon(shiftType.value);
                  const colors = getShiftTypeColor(shiftType.value);
                  const settings = systemSettings.lunch_settings?.[shiftType.value] || {
                    enabled: true,
                    duration: 30,
                    start_window: "11:00",
                    end_window: "14:00",
                    mandatory: true
                  };
                  
                  return (
                    <div key={shiftType.value} className={`bg-gradient-to-r ${colors.bg} border ${colors.border} rounded-lg p-6`}>
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center space-x-3">
                          <div className={`${colors.accent} text-white p-2 rounded-lg`}>
                            <IconComponent className="h-5 w-5" />
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">{shiftType.display_name}</h3>
                            <p className="text-sm text-gray-600">{shiftType.description}</p>
                          </div>
                        </div>
                        <Switch
                          checked={settings.enabled}
                          onCheckedChange={(checked) => updateShiftTypeLunchSetting(shiftType.value, 'enabled', checked)}
                        />
                      </div>
                      
                      {settings.enabled && (
                        <div className={`bg-white rounded-lg p-4 space-y-4 border ${colors.border}`}>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <Label className="text-sm font-medium text-gray-700">Duration</Label>
                              <Select
                                value={String(settings.duration || 30)}
                                onValueChange={(value) => updateShiftTypeLunchSetting(shiftType.value, 'duration', parseInt(value))}
                              >
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="15">15 minutes</SelectItem>
                                  <SelectItem value="30">30 minutes</SelectItem>
                                  <SelectItem value="45">45 minutes</SelectItem>
                                  <SelectItem value="60">60 minutes</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            
                            <div>
                              <Label className="text-sm font-medium text-gray-700">Window Start</Label>
                              <Input
                                type="time"
                                value={settings.start_window || "11:00"}
                                onChange={(e) => updateShiftTypeLunchSetting(shiftType.value, 'start_window', e.target.value)}
                              />
                            </div>
                            
                            <div>
                              <Label className="text-sm font-medium text-gray-700">Window End</Label>
                              <Input
                                type="time"
                                value={settings.end_window || "14:00"}
                                onChange={(e) => updateShiftTypeLunchSetting(shiftType.value, 'end_window', e.target.value)}
                              />
                            </div>
                          </div>
                          
                          <div className={`flex items-center space-x-2 pt-2 border-t ${colors.border}`}>
                            <Checkbox
                              id={`${shiftType.value}-mandatory`}
                              checked={settings.mandatory}
                              onCheckedChange={(checked) => updateShiftTypeLunchSetting(shiftType.value, 'mandatory', checked)}
                            />
                            <Label htmlFor={`${shiftType.value}-mandatory`} className="text-sm text-gray-700 cursor-pointer">
                              Lunch break is mandatory for this shift type
                            </Label>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-gray-500">
                  Loading shift types...
                </div>
              )}

              {/* Save Button */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-gray-600">
                  <p className="font-medium mb-1">Note:</p>
                  <p>Changes apply to new shifts only. Existing shifts are not affected.</p>
                </div>
                <Button
                  onClick={() => updateSystemSettings(systemSettings)}
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Save Lunch Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Information Card */}
          <Card className="bg-gray-50">
            <CardContent className="pt-6">
              <h4 className="font-semibold text-gray-900 mb-3 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2 text-blue-600" />
                About Lunch Break Settings
              </h4>
              <ul className="text-sm text-gray-700 space-y-2">
                <li className="flex items-start space-x-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span><strong>Duration:</strong> How long the lunch break lasts</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span><strong>Window:</strong> Time range during which lunch can be taken</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-blue-600 font-bold">•</span>
                  <span><strong>Mandatory:</strong> Whether the lunch break is required for this shift type</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-orange-600 font-bold">•</span>
                  <span>Lunch periods are typically unpaid time and reduce total working hours</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-purple-600 font-bold">•</span>
                  <span>Configure each shift type independently based on your organization's needs</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-green-600 font-bold">✨</span>
                  <span><strong>Auto-Discovery:</strong> New shift types added in the backend automatically appear here!</span>
                </li>
              </ul>
            </CardContent>
          </Card>
          
          {/* Flex Bank Settings Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Home className="h-5 w-5 text-indigo-600" />
                <span>Flex Bank Settings (Remote Work Policy)</span>
              </CardTitle>
              <CardDescription>
                Configure the annual Flex Bank days for work-from-home requests
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    Default Annual Flex Bank Days
                  </Label>
                  <div className="flex items-center space-x-4">
                    <Input
                      type="number"
                      min="0"
                      max="365"
                      step="1"
                      value={systemSettings.flex_bank_default_days || 15}
                      onChange={(e) => setSystemSettings({
                        ...systemSettings,
                        flex_bank_default_days: parseInt(e.target.value)
                      })}
                      className="w-24"
                    />
                    <span className="text-sm text-gray-600">days per year</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    New employees and yearly resets will use this value. Can be adjusted per employee.
                  </p>
                </div>
                
                <div className="border-t border-indigo-200 pt-4">
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    Allowed WFH Hours per Day
                  </Label>
                  <p className="text-xs text-gray-500 mb-3">
                    Select which hour options employees can choose when requesting WFH
                  </p>
                  <div className="flex space-x-4">
                    <label className={`flex items-center space-x-2 px-4 py-2 border rounded-lg cursor-pointer ${
                      (systemSettings.flex_bank_allowed_hours || [4, 8]).includes(4)
                        ? 'bg-indigo-100 border-indigo-400'
                        : 'bg-white border-gray-200'
                    }`}>
                      <input
                        type="checkbox"
                        checked={(systemSettings.flex_bank_allowed_hours || [4, 8]).includes(4)}
                        onChange={(e) => {
                          const current = systemSettings.flex_bank_allowed_hours || [4, 8];
                          const newHours = e.target.checked
                            ? [...current, 4].sort((a, b) => a - b)
                            : current.filter(h => h !== 4);
                          // Ensure at least one option is selected
                          if (newHours.length > 0) {
                            setSystemSettings({ ...systemSettings, flex_bank_allowed_hours: newHours });
                          }
                        }}
                        className="w-4 h-4 text-indigo-600 rounded"
                      />
                      <span className="font-medium">4 hours</span>
                      <span className="text-xs text-gray-500">(Half day)</span>
                    </label>
                    <label className={`flex items-center space-x-2 px-4 py-2 border rounded-lg cursor-pointer ${
                      (systemSettings.flex_bank_allowed_hours || [4, 8]).includes(8)
                        ? 'bg-indigo-100 border-indigo-400'
                        : 'bg-white border-gray-200'
                    }`}>
                      <input
                        type="checkbox"
                        checked={(systemSettings.flex_bank_allowed_hours || [4, 8]).includes(8)}
                        onChange={(e) => {
                          const current = systemSettings.flex_bank_allowed_hours || [4, 8];
                          const newHours = e.target.checked
                            ? [...current, 8].sort((a, b) => a - b)
                            : current.filter(h => h !== 8);
                          // Ensure at least one option is selected
                          if (newHours.length > 0) {
                            setSystemSettings({ ...systemSettings, flex_bank_allowed_hours: newHours });
                          }
                        }}
                        className="w-4 h-4 text-indigo-600 rounded"
                      />
                      <span className="font-medium">8 hours</span>
                      <span className="text-xs text-gray-500">(Full day)</span>
                    </label>
                  </div>
                </div>
                
                <div className="border-t border-indigo-200 pt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label className="text-sm font-medium text-gray-700">
                        Auto-Reject When Balance Depleted
                      </Label>
                      <p className="text-xs text-gray-500">
                        Automatically reject WFH requests when employee's Flex Bank balance is zero
                      </p>
                    </div>
                    <Switch
                      checked={systemSettings.flex_bank_auto_reject || false}
                      onCheckedChange={(checked) => setSystemSettings({
                        ...systemSettings,
                        flex_bank_auto_reject: checked
                      })}
                    />
                  </div>
                </div>
              </div>
              
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> Flex Bank days reset automatically on January 1st each year.
                  Unused days do not carry over. To adjust individual employee balances, edit their profile.
                </p>
              </div>
              
              <Button
                onClick={() => updateSystemSettings(systemSettings)}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Save Flex Bank Settings
              </Button>
            </CardContent>
          </Card>

          {/* On-Call WFH Auto-Conversion Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Phone className="h-5 w-5 text-orange-600" />
                <span>On-Call WFH Auto-Conversion</span>
              </CardTitle>
              <CardDescription>
                Configure automatic work-from-home assignment when employees are on-call
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Label className="text-sm font-medium text-gray-700">
                      Enable Auto-WFH for On-Call
                    </Label>
                    <p className="text-xs text-gray-500">
                      Automatically set regular shifts to WFH when the employee has an on-call shift
                    </p>
                  </div>
                  <Switch
                    checked={systemSettings.oncall_wfh_enabled !== false}
                    onCheckedChange={(checked) => setSystemSettings({
                      ...systemSettings,
                      oncall_wfh_enabled: checked
                    })}
                    data-testid="oncall-wfh-enabled-toggle"
                  />
                </div>

                {systemSettings.oncall_wfh_enabled !== false && (
                  <>
                    <div className="border-t border-orange-200 pt-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <Label className="text-sm font-medium text-gray-700">
                            Exclude Remote Employees
                          </Label>
                          <p className="text-xs text-gray-500">
                            Remote employees are already working from home and don't need WFH conversion
                          </p>
                        </div>
                        <Switch
                          checked={systemSettings.oncall_wfh_exclude_remote !== false}
                          onCheckedChange={(checked) => setSystemSettings({
                            ...systemSettings,
                            oncall_wfh_exclude_remote: checked
                          })}
                          data-testid="oncall-wfh-exclude-remote-toggle"
                        />
                      </div>
                    </div>

                    <div className="border-t border-orange-200 pt-4">
                      <Label className="text-sm font-medium text-gray-700 mb-2 block">
                        Excluded Roles
                      </Label>
                      <p className="text-xs text-gray-500 mb-3">
                        Select roles that should NOT be auto-converted to WFH when on-call
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {systemRoles.map(role => {
                          const isExcluded = (systemSettings.oncall_wfh_excluded_roles || []).includes(role.id);
                          return (
                            <label
                              key={role.id}
                              className={`flex items-center space-x-2 px-3 py-1.5 border rounded-lg cursor-pointer text-sm transition-colors ${
                                isExcluded
                                  ? 'bg-orange-100 border-orange-400 text-orange-800'
                                  : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isExcluded}
                                onChange={(e) => {
                                  const current = systemSettings.oncall_wfh_excluded_roles || [];
                                  const updated = e.target.checked
                                    ? [...current, role.id]
                                    : current.filter(r => r !== role.id);
                                  setSystemSettings({ ...systemSettings, oncall_wfh_excluded_roles: updated });
                                }}
                                className="w-3.5 h-3.5 text-orange-600 rounded"
                              />
                              <span>{role.label || role.id}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </div>
              
              <Button
                onClick={() => updateSystemSettings(systemSettings)}
                className="bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="save-oncall-wfh-settings"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Save On-Call WFH Settings
              </Button>
            </CardContent>
          </Card>
          
          {/* Team Flex Bank Overview */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <Users className="h-5 w-5 text-blue-600" />
                    <span>Team Flex Bank Balances</span>
                  </CardTitle>
                  <CardDescription>
                    View and manage Flex Bank balances for all team members
                  </CardDescription>
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      if (!window.confirm('This will recalculate ALL office employees\' Flex Bank balances based on their approved WFH requests. Continue?')) return;
                      try {
                        const token = localStorage.getItem('token');
                        const headers = { Authorization: `Bearer ${token}` };
                        const response = await axios.post(`${API}/flex-bank/recalculate-all`, {}, { headers });
                        toast.success(`Recalculated ${response.data.fixed_count} balances`);
                        if (response.data.changes.length > 0) {
                          console.log('Balance changes:', response.data.changes);
                        }
                        fetchTeamFlexBank();
                      } catch (error) {
                        toast.error('Failed to recalculate balances');
                        console.error(error);
                      }
                    }}
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Recalculate All
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={fetchTeamFlexBank}
                    disabled={loadingFlexBank}
                  >
                    {loadingFlexBank ? 'Loading...' : 'Refresh'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingFlexBank ? (
                <div className="text-center py-8">
                  <Clock className="h-8 w-8 mx-auto mb-2 animate-spin text-gray-400" />
                  <p className="text-gray-500">Loading team balances...</p>
                </div>
              ) : teamFlexBank.length === 0 ? (
                <div className="text-center py-8">
                  <Users className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p className="text-gray-500">Click "Refresh" to load team Flex Bank balances</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Summary Stats */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-green-700">
                        {teamFlexBank.filter(u => u.flex_bank_balance > 5).length}
                      </p>
                      <p className="text-xs text-green-600">Healthy Balance (6+)</p>
                    </div>
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-yellow-700">
                        {teamFlexBank.filter(u => u.flex_bank_balance > 0 && u.flex_bank_balance <= 5).length}
                      </p>
                      <p className="text-xs text-yellow-600">Low Balance (1-5)</p>
                    </div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                      <p className="text-2xl font-bold text-red-700">
                        {teamFlexBank.filter(u => u.flex_bank_balance === 0).length}
                      </p>
                      <p className="text-xs text-red-600">Depleted (0)</p>
                    </div>
                  </div>
                  
                  {/* Employee List */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                          <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Balance</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Default</th>
                          <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {teamFlexBank.map((member) => (
                          <tr key={member.user_id} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{member.name}</td>
                            <td className="px-4 py-3 text-sm text-gray-500 capitalize">{member.role}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                member.flex_bank_balance > 5 
                                  ? 'bg-green-100 text-green-800' 
                                  : member.flex_bank_balance > 0 
                                    ? 'bg-yellow-100 text-yellow-800' 
                                    : 'bg-red-100 text-red-800'
                              }`}>
                                {member.flex_bank_balance} days
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center text-sm text-gray-500">{member.default_days}</td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center space-x-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => updateUserFlexBank(member.user_id, Math.max(0, member.flex_bank_balance - 1))}
                                  className="h-6 w-6 p-0"
                                  title="Decrease by 1"
                                >
                                  -
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => updateUserFlexBank(member.user_id, member.flex_bank_balance + 1)}
                                  className="h-6 w-6 p-0"
                                  title="Increase by 1"
                                >
                                  +
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => updateUserFlexBank(member.user_id, member.default_days)}
                                  className="text-xs px-2"
                                  title="Reset to default"
                                >
                                  Reset
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={async () => {
                                    try {
                                      const token = localStorage.getItem('token');
                                      const headers = { Authorization: `Bearer ${token}` };
                                      const response = await axios.post(`${API}/flex-bank/recalculate/${member.user_id}`, {}, { headers });
                                      toast.success(`${member.name}: ${response.data.old_balance} → ${response.data.new_balance} (${response.data.total_used} days used)`);
                                      fetchTeamFlexBank();
                                    } catch (error) {
                                      toast.error('Failed to recalculate');
                                    }
                                  }}
                                  className="text-xs px-2 text-blue-600"
                                  title="Recalculate from approved WFH requests"
                                >
                                  Recalc
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Integrations Tab */}
      {activeTab === 'integrations' && (
        <div className="space-y-6" data-testid="integrations-tab-content">
          <IntercomPanel />
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Settings className="h-5 w-5 text-violet-600" />
                <span>Feature Toggles</span>
              </CardTitle>
              <CardDescription>Enable or disable optional features for your organization</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-amber-100">
                    <Cake className="h-4 w-4 text-amber-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Dashboard Celebrations</p>
                    <p className="text-xs text-gray-500">Show birthdays and work anniversaries on the dashboard</p>
                  </div>
                </div>
                <Switch
                  data-testid="toggle-celebrations"
                  checked={systemSettings.features?.celebrations !== false}
                  onCheckedChange={(checked) => {
                    const newSettings = {
                      ...systemSettings,
                      features: {
                        ...systemSettings.features,
                        celebrations: checked
                      }
                    };
                    setSystemSettings(newSettings);
                    updateSystemSettings(newSettings);
                  }}
                />
              </div>

              {systemSettings.features?.celebrations !== false && (
                <div className="p-3 bg-amber-50/60 rounded-lg border border-amber-100 ml-0 sm:ml-10">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Celebrations display window</p>
                      <p className="text-xs text-gray-500">How far ahead the dashboard shows birthdays & anniversaries. This week's are always highlighted.</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={systemSettings.celebrations_window || 'month'}
                        onValueChange={(value) => {
                          const newSettings = { ...systemSettings, celebrations_window: value };
                          setSystemSettings(newSettings);
                          updateSystemSettings(newSettings);
                        }}
                      >
                        <SelectTrigger className="w-44" data-testid="celebrations-window-select">
                          <SelectValue placeholder="Select window" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="week">This week (7 days)</SelectItem>
                          <SelectItem value="month">This month</SelectItem>
                          <SelectItem value="days">Custom days…</SelectItem>
                        </SelectContent>
                      </Select>
                      {systemSettings.celebrations_window === 'days' && (
                        <input
                          type="number"
                          min="1"
                          max="365"
                          data-testid="celebrations-window-days-input"
                          className="w-20 px-2 py-1.5 border border-gray-300 rounded-md text-sm"
                          value={systemSettings.celebrations_window_days || 30}
                          onChange={(e) => setSystemSettings({ ...systemSettings, celebrations_window_days: parseInt(e.target.value) || 30 })}
                          onBlur={() => updateSystemSettings(systemSettings)}
                        />
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-100">
                    <Clock className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Time Tracking</p>
                    <p className="text-xs text-gray-500">Enable time clock, timecards, and payroll export</p>
                  </div>
                </div>
                <Switch
                  data-testid="toggle-time-tracking"
                  checked={systemSettings.features?.time_tracking !== false}
                  onCheckedChange={(checked) => {
                    const newSettings = {
                      ...systemSettings,
                      features: {
                        ...systemSettings.features,
                        time_tracking: checked
                      }
                    };
                    setSystemSettings(newSettings);
                    updateSystemSettings(newSettings);
                  }}
                />
              </div>

              <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-100">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-emerald-100">
                    <Phone className="h-4 w-4 text-emerald-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Roster &amp; Contacts</p>
                    <p className="text-xs text-gray-500">Team directory showing who's on call now and how to reach them (visible to all roles)</p>
                  </div>
                </div>
                <Switch
                  data-testid="toggle-directory"
                  checked={systemSettings.features?.directory !== false}
                  onCheckedChange={(checked) => {
                    const newSettings = {
                      ...systemSettings,
                      features: {
                        ...systemSettings.features,
                        directory: checked
                      }
                    };
                    setSystemSettings(newSettings);
                    updateSystemSettings(newSettings);
                  }}
                />
              </div>

              {systemSettings.features?.directory !== false && (
                <div className="p-3 bg-emerald-50/60 rounded-lg border border-emerald-100 ml-0 sm:ml-10">
                  <p className="font-medium text-gray-900 text-sm">Hide contact fields</p>
                  <p className="text-xs text-gray-500 mb-3">All contact info is shown by default. Turn a field on to hide it from the Roster &amp; Contacts page.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {[
                      { key: 'email', label: 'Email' },
                      { key: 'work_phone', label: 'Work phone' },
                      { key: 'phone', label: 'Mobile phone' },
                      { key: 'home_phone', label: 'Home phone' },
                    ].map((f) => {
                      const hidden = (systemSettings.directory_hidden_fields || []).includes(f.key);
                      return (
                        <div key={f.key} className="flex items-center justify-between p-2 bg-white rounded-md border border-gray-200">
                          <span className="text-sm text-gray-700">Hide {f.label}</span>
                          <Switch
                            data-testid={`toggle-hide-${f.key}`}
                            checked={hidden}
                            onCheckedChange={(checked) => {
                              const cur = systemSettings.directory_hidden_fields || [];
                              const updated = checked
                                ? Array.from(new Set([...cur, f.key]))
                                : cur.filter((k) => k !== f.key);
                              const newSettings = { ...systemSettings, directory_hidden_fields: updated };
                              setSystemSettings(newSettings);
                              updateSystemSettings(newSettings);
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-slate-200">
                    <Clock className="h-4 w-4 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 text-sm">Auto sign-out on inactivity</p>
                    <p className="text-xs text-gray-500">When on, users are signed out after a period of no activity (a warning appears first). Off by default — sessions last the full 24 hours.</p>
                  </div>
                </div>
                <Switch
                  data-testid="toggle-idle-timeout"
                  checked={systemSettings.idle_timeout_enabled === true}
                  onCheckedChange={(checked) => {
                    const newSettings = { ...systemSettings, idle_timeout_enabled: checked };
                    setSystemSettings(newSettings);
                    updateSystemSettings(newSettings);
                  }}
                />
              </div>

              {systemSettings.idle_timeout_enabled === true && (
                <div className="p-3 bg-slate-50/60 rounded-lg border border-slate-200 ml-0 sm:ml-10">
                  <p className="font-medium text-gray-900 text-sm mb-1">Sign out after</p>
                  <p className="text-xs text-gray-500 mb-3">of inactivity.</p>
                  <Select
                    value={String(systemSettings.idle_timeout_minutes || 30)}
                    onValueChange={(val) => {
                      const newSettings = { ...systemSettings, idle_timeout_minutes: parseInt(val, 10) };
                      setSystemSettings(newSettings);
                      updateSystemSettings(newSettings);
                    }}
                  >
                    <SelectTrigger className="w-full sm:w-56" data-testid="idle-timeout-minutes">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="15">15 minutes</SelectItem>
                      <SelectItem value="30">30 minutes</SelectItem>
                      <SelectItem value="60">1 hour</SelectItem>
                      <SelectItem value="120">2 hours</SelectItem>
                      <SelectItem value="240">4 hours</SelectItem>
                      <SelectItem value="480">8 hours</SelectItem>
                      <SelectItem value="720">12 hours</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Escalation Policy */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Bell className="h-5 w-5 text-amber-600" />
                <span>Escalation Policy</span>
              </CardTitle>
              <CardDescription>Configure automated reminders and escalation for pending PTO/WFH requests</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Reminders Section */}
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-100 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-amber-100">
                      <Bell className="h-4 w-4 text-amber-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Reminders</p>
                      <p className="text-xs text-gray-500">Send reminder emails to managers for overdue pending requests</p>
                    </div>
                  </div>
                  <Switch
                    data-testid="toggle-reminders"
                    checked={systemSettings.escalation_policy?.reminders_enabled || false}
                    onCheckedChange={(checked) => {
                      const newPolicy = { ...(systemSettings.escalation_policy || {}), reminders_enabled: checked };
                      setSystemSettings({ ...systemSettings, escalation_policy: newPolicy });
                    }}
                  />
                </div>
                {systemSettings.escalation_policy?.reminders_enabled && (
                  <div className="ml-12 space-y-3">
                    <div className="flex items-center gap-3">
                      <Label className="text-sm text-gray-600 w-48">Send first reminder after</Label>
                      <Input type="number" min={1} max={168} className="w-20"
                        data-testid="reminder-after-hours"
                        value={systemSettings.escalation_policy?.reminder_after_hours || 24}
                        onChange={(e) => {
                          const newPolicy = { ...(systemSettings.escalation_policy || {}), reminder_after_hours: parseInt(e.target.value) || 24 };
                          setSystemSettings({ ...systemSettings, escalation_policy: newPolicy });
                        }} />
                      <span className="text-sm text-gray-500">hours</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Label className="text-sm text-gray-600 w-48">Repeat reminders</Label>
                      <Switch
                        data-testid="toggle-reminder-repeat"
                        checked={systemSettings.escalation_policy?.reminder_repeat_enabled || false}
                        onCheckedChange={(checked) => {
                          const newPolicy = { ...(systemSettings.escalation_policy || {}), reminder_repeat_enabled: checked };
                          setSystemSettings({ ...systemSettings, escalation_policy: newPolicy });
                        }}
                      />
                    </div>
                    {systemSettings.escalation_policy?.reminder_repeat_enabled && (
                      <>
                        <div className="flex items-center gap-3">
                          <Label className="text-sm text-gray-600 w-48">Repeat every</Label>
                          <Input type="number" min={1} max={168} className="w-20"
                            data-testid="reminder-repeat-hours"
                            value={systemSettings.escalation_policy?.reminder_repeat_every_hours || 24}
                            onChange={(e) => {
                              const newPolicy = { ...(systemSettings.escalation_policy || {}), reminder_repeat_every_hours: parseInt(e.target.value) || 24 };
                              setSystemSettings({ ...systemSettings, escalation_policy: newPolicy });
                            }} />
                          <span className="text-sm text-gray-500">hours</span>
                        </div>
                        <div className="flex items-center gap-3">
                          <Label className="text-sm text-gray-600 w-48">Max reminders before stop</Label>
                          <Input type="number" min={1} max={10} className="w-20"
                            data-testid="reminder-max-count"
                            value={systemSettings.escalation_policy?.reminder_max_count || 3}
                            onChange={(e) => {
                              const newPolicy = { ...(systemSettings.escalation_policy || {}), reminder_max_count: parseInt(e.target.value) || 3 };
                              setSystemSettings({ ...systemSettings, escalation_policy: newPolicy });
                            }} />
                          <span className="text-sm text-gray-500">reminders</span>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>

              {/* Escalation Section */}
              <div className="p-4 bg-red-50 rounded-lg border border-red-100 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-red-100">
                      <ArrowUpRight className="h-4 w-4 text-red-600" />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 text-sm">Escalation</p>
                      <p className="text-xs text-gray-500">Automatically escalate unprocessed requests to the next person in the chain</p>
                    </div>
                  </div>
                  <Switch
                    data-testid="toggle-escalation"
                    checked={systemSettings.escalation_policy?.escalation_enabled || false}
                    onCheckedChange={(checked) => {
                      const newPolicy = { ...(systemSettings.escalation_policy || {}), escalation_enabled: checked };
                      setSystemSettings({ ...systemSettings, escalation_policy: newPolicy });
                    }}
                  />
                </div>
                {systemSettings.escalation_policy?.escalation_enabled && (
                  <div className="ml-12 space-y-3">
                    <div className="flex items-center gap-3">
                      <Label className="text-sm text-gray-600 w-48">Escalate after</Label>
                      <Input type="number" min={1} max={336} className="w-20"
                        data-testid="escalation-after-hours"
                        value={systemSettings.escalation_policy?.escalation_after_hours || 48}
                        onChange={(e) => {
                          const newPolicy = { ...(systemSettings.escalation_policy || {}), escalation_after_hours: parseInt(e.target.value) || 48 };
                          setSystemSettings({ ...systemSettings, escalation_policy: newPolicy });
                        }} />
                      <span className="text-sm text-gray-500">hours without action</span>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-600 block mb-2">Escalation target</Label>
                      <Select
                        value={systemSettings.escalation_policy?.escalation_target || 'manager_chain'}
                        onValueChange={(value) => {
                          const newPolicy = { ...(systemSettings.escalation_policy || {}), escalation_target: value };
                          setSystemSettings({ ...systemSettings, escalation_policy: newPolicy });
                        }}>
                        <SelectTrigger className="w-80" data-testid="escalation-target">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manager_chain">Manager's manager (up the chain)</SelectItem>
                          <SelectItem value="org_admin">Org admin / owner</SelectItem>
                          <SelectItem value="specific_user">Specific user</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    {systemSettings.escalation_policy?.escalation_target === 'specific_user' && (
                      <div>
                        <Label className="text-sm text-gray-600 block mb-2">Escalation contact</Label>
                        <Select
                          value={systemSettings.escalation_policy?.escalation_contact_user_id || undefined}
                          onValueChange={(value) => {
                            const newPolicy = { ...(systemSettings.escalation_policy || {}), escalation_contact_user_id: value };
                            setSystemSettings({ ...systemSettings, escalation_policy: newPolicy });
                          }}>
                          <SelectTrigger className="w-80" data-testid="escalation-contact">
                            <SelectValue placeholder="Select a user..." />
                          </SelectTrigger>
                          <SelectContent>
                            {users.filter(u => u.is_active && u.role !== 'viewer').map(u => (
                              <SelectItem key={u.id} value={u.id}>{u.name || u.username} ({u.role}) — {u.email}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Button
                onClick={() => updateSystemSettings(systemSettings)}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="save-escalation-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Save Escalation Policy
              </Button>
            </CardContent>
          </Card>


          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Monitor className="h-5 w-5 text-blue-600" />
                <span>Dashboard Settings</span>
              </CardTitle>
              <CardDescription>Configure how the Today dashboard displays shift information</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <Label className="text-sm font-medium text-gray-700 mb-2 block">
                    "Ending Soon" Time Window
                  </Label>
                  <div className="flex items-center space-x-4">
                    <Input
                      type="number"
                      min="0.5"
                      max="8"
                      step="0.5"
                      value={systemSettings.ending_soon_hours || 1.0}
                      onChange={(e) => setSystemSettings({
                        ...systemSettings,
                        ending_soon_hours: parseFloat(e.target.value)
                      })}
                      className="w-24"
                    />
                    <span className="text-sm text-gray-600">hours before shift end</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    Shows shifts ending within this time window on the Today dashboard
                  </p>
                </div>
                
                <Button
                  onClick={() => updateSystemSettings(systemSettings)}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Save Dashboard Settings
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Feedback Management Tab */}
      {activeTab === 'feedback' && (
        <FeedbackManagement />
      )}
    </div>
  );
};

// Feedback Management Component
const FeedbackManagement = () => {
  const { token } = useAuth();
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [newStatus, setNewStatus] = useState('');

  const headers = { Authorization: `Bearer ${token}` };

  const fetchFeedback = async () => {
    setLoading(true);
    try {
      let url = `${API}/feedback`;
      const params = [];
      if (statusFilter) params.push(`status=${statusFilter}`);
      if (typeFilter) params.push(`type=${typeFilter}`);
      if (params.length) url += '?' + params.join('&');
      
      const response = await axios.get(url, { headers });
      setFeedback(response.data);
    } catch (error) {
      toast.error('Failed to load feedback');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeedback();
  }, [statusFilter, typeFilter]);

  const handleUpdateFeedback = async (feedbackId) => {
    try {
      const params = [];
      if (newStatus) params.push(`status=${newStatus}`);
      if (adminNotes) params.push(`admin_notes=${encodeURIComponent(adminNotes)}`);
      
      await axios.put(`${API}/feedback/${feedbackId}?${params.join('&')}`, {}, { headers });
      toast.success('Feedback updated');
      setEditingFeedback(null);
      setAdminNotes('');
      setNewStatus('');
      fetchFeedback();
    } catch (error) {
      toast.error('Failed to update feedback');
    }
  };

  const handleDeleteFeedback = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) return;
    
    try {
      await axios.delete(`${API}/feedback/${feedbackId}`, { headers });
      toast.success('Feedback deleted');
      fetchFeedback();
    } catch (error) {
      toast.error('Failed to delete feedback');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      new: 'bg-blue-100 text-blue-800',
      in_progress: 'bg-yellow-100 text-yellow-800',
      resolved: 'bg-green-100 text-green-800',
      closed: 'bg-gray-100 text-gray-800',
      wont_do: 'bg-red-100 text-red-800'
    };
    const labels = {
      new: 'New',
      in_progress: 'In Progress',
      resolved: 'Resolved',
      closed: 'Closed',
      wont_do: "Won't Do"
    };
    return <Badge className={styles[status] || styles.new}>{labels[status] || status}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      low: 'bg-gray-100 text-gray-600',
      medium: 'bg-yellow-100 text-yellow-700',
      high: 'bg-red-100 text-red-700'
    };
    return <Badge className={styles[priority] || styles.medium}>{priority}</Badge>;
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <MessageSquare className="h-5 w-5" />
            <span>User Feedback Management</span>
          </CardTitle>
          <p className="text-sm text-gray-600">Review and respond to bug reports and enhancement requests from users.</p>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium">Status:</span>
              <Select value={statusFilter || 'all'} onValueChange={(v) => setStatusFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="new">New</SelectItem>
                  <SelectItem value="in_progress">In Progress</SelectItem>
                  <SelectItem value="resolved">Resolved</SelectItem>
                  <SelectItem value="closed">Closed</SelectItem>
                  <SelectItem value="wont_do">Won't Do</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium">Type:</span>
              <Select value={typeFilter || 'all'} onValueChange={(v) => setTypeFilter(v === 'all' ? '' : v)}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="bug">Bug Reports</SelectItem>
                  <SelectItem value="enhancement">Enhancements</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={fetchFeedback}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          </div>

          {/* Feedback List */}
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading feedback...</div>
          ) : feedback.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <MessageSquare className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>No feedback submissions found.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {feedback.map((item) => (
                <div key={item.id} className="border rounded-lg p-4 bg-white">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center flex-wrap gap-2 mb-2">
                        <Badge className={item.type === 'bug' ? 'bg-red-100 text-red-800' : 'bg-purple-100 text-purple-800'}>
                          {item.type === 'bug' ? '🐛 Bug' : '💡 Enhancement'}
                        </Badge>
                        {getStatusBadge(item.status)}
                        {getPriorityBadge(item.priority)}
                      </div>
                      <h4 className="font-semibold text-gray-900">{item.title}</h4>
                      <p className="text-gray-600 mt-1 whitespace-pre-wrap">{item.description}</p>
                      
                      {/* Screenshot */}
                      {item.screenshot && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-gray-500 mb-1">Screenshot:</p>
                          <img 
                            src={item.screenshot} 
                            alt="User screenshot" 
                            className="max-h-60 rounded-lg border cursor-pointer hover:opacity-90"
                            onClick={() => window.open(item.screenshot, '_blank')}
                          />
                        </div>
                      )}
                      
                      {/* System Info */}
                      {item.system_info && (
                        <details className="mt-3">
                          <summary className="text-xs font-medium text-gray-500 cursor-pointer hover:text-gray-700">
                            🖥️ System Info (click to expand)
                          </summary>
                          <div className="mt-2 p-2 bg-gray-100 rounded text-xs font-mono space-y-1">
                            <p><strong>Browser:</strong> {item.system_info.userAgent?.split(' ').slice(-2).join(' ')}</p>
                            <p><strong>Platform:</strong> {item.system_info.platform}</p>
                            <p><strong>Screen:</strong> {item.system_info.screenWidth}x{item.system_info.screenHeight}</p>
                            <p><strong>Viewport:</strong> {item.system_info.viewportWidth}x{item.system_info.viewportHeight}</p>
                            <p><strong>Timezone:</strong> {item.system_info.timezone}</p>
                            {item.current_page && <p><strong>Page:</strong> {item.current_page}</p>}
                          </div>
                        </details>
                      )}
                      
                      <div className="mt-2 text-sm text-gray-500">
                        <span>From: <strong>{item.user_name}</strong> ({item.user_email})</span>
                        <span className="mx-2">•</span>
                        <span>{new Date(item.created_at).toLocaleString()}</span>
                      </div>
                      {item.admin_notes && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                          <span className="text-sm font-medium text-blue-800">Admin Notes:</span>
                          <p className="text-sm text-blue-700">{item.admin_notes}</p>
                        </div>
                      )}
                      {item.resolved_by && (
                        <div className="mt-2 text-sm text-green-600">
                          Resolved by {item.resolved_by} on {new Date(item.resolved_at).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setEditingFeedback(item.id);
                          setNewStatus(item.status);
                          setAdminNotes(item.admin_notes || '');
                        }}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => handleDeleteFeedback(item.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Edit Form */}
                  {editingFeedback === item.id && (
                    <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium mb-1">Status</label>
                          <Select value={newStatus} onValueChange={setNewStatus}>
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="new">New</SelectItem>
                              <SelectItem value="in_progress">In Progress</SelectItem>
                              <SelectItem value="resolved">Resolved</SelectItem>
                              <SelectItem value="closed">Closed</SelectItem>
                              <SelectItem value="wont_do">Won't Do</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="mt-3">
                        <label className="block text-sm font-medium mb-1">Admin Notes / Response</label>
                        <Textarea
                          value={adminNotes}
                          onChange={(e) => setAdminNotes(e.target.value)}
                          placeholder="Add notes or response for the user..."
                          rows={3}
                        />
                      </div>
                      <div className="mt-3 flex justify-end space-x-2">
                        <Button variant="outline" onClick={() => setEditingFeedback(null)}>
                          Cancel
                        </Button>
                        <Button onClick={() => handleUpdateFeedback(item.id)}>
                          Save Changes
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default Admin;